import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { fabric } from 'fabric';
import { jsPDF } from 'jspdf';
import AulaService from '../services/AulaService';
import apiService from '../services/ApiService';
import resolveHubBaseUrl from '../utils/hubBaseUrl';
import materiaService from '../services/MateriaService';
import aulaSessionService from '../services/AulaSessionService';
import { useAuth } from '../contexts/AuthContext';
import ChatModal from './common/ChatModal';
import useAulaHubPresence from '../hooks/useAulaHubPresence';
import { AulaEmptyState, AulaNav, AulaOverview, AulaSection } from './common/AulaShell';
import logoAnimacion from '../assets/logos/logoAnimacion.png';
import './AulaPublicPage.css';

const BOARD_MIN_ZOOM = 0.3;
const BOARD_MAX_ZOOM = 3.5;
const BOARD_OBJECT_JSON_PROPS = [
  'src',
  'left',
  'top',
  'scaleX',
  'scaleY',
  'angle',
  'width',
  'height',
  'text',
  'fontSize',
  'fontFamily',
  'fontWeight',
  'fontStyle',
  'underline',
  'fill',
  'textAlign',
  'charSpacing',
  'lineHeight',
  'editable'
];
const AULA_TELEMETRY_SESSION_KEY = 'colegiosopenai:aula-public-telemetry-session-id';

const isBoardEditableObject = (object) => ['image', 'i-text', 'textbox', 'text'].includes(object?.type);

const isBoardTextObject = (object) => ['i-text', 'textbox', 'text'].includes(object?.type);

const getBoardObjectJson = (object) => object.toJSON(BOARD_OBJECT_JSON_PROPS);

const getBoardSelectionObjects = (object) => {
  if (!object) return [];
  if (object.type === 'activeSelection' && typeof object.getObjects === 'function') {
    return object.getObjects();
  }
  return [object];
};

const applyBoardObjectInteractivity = (object, canEdit) => {
  const editableObject = isBoardEditableObject(object);
  object.selectable = Boolean(canEdit && editableObject);
  object.evented = Boolean(canEdit && editableObject);
  if ('editable' in object) {
    object.editable = Boolean(canEdit && editableObject);
  }
};

const createAnonymousSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getOrCreateAulaTelemetrySessionId = () => {
  if (typeof window === 'undefined') {
    return createAnonymousSessionId();
  }

  try {
    const storage = window.sessionStorage;
    const existing = storage.getItem(AULA_TELEMETRY_SESSION_KEY);
    if (existing) return existing;
    const nextValue = createAnonymousSessionId();
    storage.setItem(AULA_TELEMETRY_SESSION_KEY, nextValue);
    return nextValue;
  } catch (_) {
    return createAnonymousSessionId();
  }
};

const getCurrentPublicRoute = () => {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}`;
};

const getCookieValue = (name) => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  return parts.length === 2 ? parts.pop().split(';').shift() : null;
};

const getJsonCookieValue = (name) => {
  const raw = getCookieValue(name);
  if (!raw) return null;
  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch (_) {
    return null;
  }
};

const buildMainAuthSnapshot = (currentUser) => {
  const cookieUser = getJsonCookieValue('user_data') || {};
  const rawToken = getCookieValue('auth_token');
  const rawRoleName = getCookieValue('user_role_name');

  const token = currentUser?.token || (rawToken ? decodeURIComponent(rawToken) : '') || cookieUser?.token || '';
  const roleName =
    currentUser?.roleName ||
    currentUser?.nombreRol ||
    currentUser?.role ||
    (rawRoleName ? decodeURIComponent(rawRoleName) : '') ||
    cookieUser?.roleName ||
    cookieUser?.nombreRol ||
    cookieUser?.role ||
    '';

  return {
    token,
    user: {
      token,
      idUsuario: currentUser?.idUsuario || currentUser?.userId || cookieUser?.idUsuario || cookieUser?.userId || null,
      idRol: currentUser?.idRol || currentUser?.roleId || cookieUser?.idRol || cookieUser?.roleId || null,
      nombreRol: roleName,
      displayName:
        currentUser?.displayName ||
        currentUser?.name ||
        cookieUser?.displayName ||
        cookieUser?.name ||
        currentUser?.email ||
        cookieUser?.email ||
        '',
      nombre: currentUser?.nombre || cookieUser?.nombre || '',
      apellido: currentUser?.apellido || cookieUser?.apellido || '',
      email: currentUser?.email || cookieUser?.email || ''
    }
  };
};

const parseBooleanQueryValue = (rawValue) => {
  if (rawValue === null || rawValue === undefined) {
    return null;
  }

  const normalized = String(rawValue).trim().toLowerCase();
  if (['1', 'true', 'si', 'sí', 'publica', 'publico', 'public'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'no', 'privada', 'privado', 'private'].includes(normalized)) {
    return false;
  }

  return null;
};

const normalizeAulaEstadoPayload = (payload = {}, fallbackAulaId = '') => ({
  aulaId: payload.aulaId || payload.AulaId || payload.id || payload.Id || fallbackAulaId,
  idColegio: payload.idColegio || payload.IdColegio || null,
  aulaPublica: payload.aulaPublica ?? payload.AulaPublica ?? false,
  idCurso: payload.idCurso || payload.IdCurso || null,
  nombreCurso: payload.nombreCurso || payload.NombreCurso || '',
  requiereAutenticacion: payload.requiereAutenticacion ?? payload.RequiereAutenticacion ?? false,
  idMateria: payload.idMateria || payload.IdMateria || null,
  nombreMateria: payload.nombreMateria || payload.NombreMateria || '',
  cicloLectivo: payload.cicloLectivo || payload.CicloLectivo || null,
  permiteChatGrupalEnAula: payload.permiteChatGrupalEnAula ?? payload.PermiteChatGrupalEnAula ?? false,
  permiteIniciarVideoLlamadaEnAula:
    payload.permiteIniciarVideoLlamadaEnAula ?? payload.PermiteIniciarVideoLlamadaEnAula ?? false,
  permiteSubirArchivosEnAula:
    payload.permiteSubirArchivosEnAula ??
    payload.PermiteSubirArchivosEnAula ??
    payload.permiteSubirArchivosAlAula ??
    payload.PermiteSubirArchivosAlAula ??
    false,
  permitePizarraCompartidaEnAula:
    payload.permitePizarraCompartidaEnAula ?? payload.PermitePizarraCompartidaEnAula ?? false,
  permiteLinksEnAula: payload.permiteLinksEnAula ?? payload.PermiteLinksEnAula ?? true,
  permiteForoEnAula:
    payload.permiteForoEnAula ??
    payload.PermiteForoEnAula ??
    payload.permiteEnviarMensajesEnAula ??
    payload.PermiteEnviarMensajesEnAula ??
    true,
  mostrarAsistentesIA: payload.mostrarAsistentesIA ?? payload.MostrarAsistentesIA ?? true,
  permiteChatsOpenAI: payload.permiteChatsOpenAI ?? payload.PermiteChatsOpenAI ?? false,
  permiteAgentesClasicosGPT: payload.permiteAgentesClasicosGPT ?? payload.PermiteAgentesClasicosGPT ?? false
});

const getAssistantId = (assistant) =>
  assistant?.idOpenAIAssistant ||
  assistant?.IdOpenAIAssistant ||
  assistant?.id ||
  assistant?.Id ||
  assistant?.nicknameUrl ||
  assistant?.NicknameUrl ||
  assistant?.nombre ||
  assistant?.Nombre ||
  '';

const isClassicAssistant = (assistant) =>
  assistant?.esGPTClasico ??
  assistant?.EsGPTClasico ??
  assistant?.gptClasicoCreacionFinalizada ??
  assistant?.GPTClasicoCreacionFinalizada ??
  Boolean(assistant?.gptClasicoModelo || assistant?.GPTClasicoModelo);

const hasClassicAssistantPublicUrl = (assistant) => {
  if (!isClassicAssistant(assistant)) {
    return true;
  }

  const publicUrl =
    assistant?.gptClasicoURLPublica ||
    assistant?.GPTClasicoURLPublica ||
    assistant?.gptClasicoUrlPublica ||
    assistant?.GptClasicoUrlPublica ||
    '';

  return Boolean(String(publicUrl).trim());
};

const normalizeLinkResource = (item) => ({
  id: item?.id || item?.Id || '',
  idAula: item?.idAula || item?.IdAula || '',
  url: item?.url || item?.Url || '',
  descripcion: item?.descripcion || item?.Descripcion || '',
  fechaAlta: item?.fechaAlta || item?.FechaAlta || null,
  fechaModificacion: item?.fechaModificacion || item?.FechaModificacion || null
});

const getLinkHostname = (url) => {
  try {
    return new URL(String(url || '')).hostname.replace(/^www\./i, '');
  } catch (_) {
    return '';
  }
};

const normalizeAssistantCollections = (payload) => {
  const klassics = payload?.asistentesGptClasicos ?? payload?.asistentesGPTClasicos ?? payload?.AsistentesGPTClasicos;
  const openAI = payload?.asistentesOpenAI ?? payload?.AsistentesOpenAI;
  const combined = payload?.asistentes ?? payload?.Asistentes ?? [];

  const classicAssistantsRaw = Array.isArray(klassics)
    ? klassics
    : Array.isArray(combined)
      ? combined.filter((assistant) => isClassicAssistant(assistant))
      : [];

  const openAIAssistants = Array.isArray(openAI)
    ? openAI
    : Array.isArray(combined)
      ? combined.filter((assistant) => !isClassicAssistant(assistant))
      : [];

  const classicAssistants = classicAssistantsRaw.filter(hasClassicAssistantPublicUrl);

  return { classicAssistants, openAIAssistants };
};

const AulaPublicPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: appUser } = useAuth();
  const [aulaId, setAulaId] = useState('');
  const [resolvedAulaPublicHint, setResolvedAulaPublicHint] = useState(null);
  const [loadingEstado, setLoadingEstado] = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [estadoError, setEstadoError] = useState('');
  const [aulaEstado, setAulaEstado] = useState(null);
  const [aulaDetalle, setAulaDetalle] = useState(null);
  const [aulaAuthToken, setAulaAuthToken] = useState(() => aulaSessionService.getToken());
  const [aulaAuthUser, setAulaAuthUser] = useState(() => aulaSessionService.getUser());
  const [classicAssistants, setClassicAssistants] = useState([]);
  const [openAIAssistants, setOpenAIAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [assistantPassword, setAssistantPassword] = useState('');
  const [assistantPasswordError, setAssistantPasswordError] = useState('');
  const [assistantValidating, setAssistantValidating] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatPassword, setChatPassword] = useState('');
  const [showConsent, setShowConsent] = useState(false);
  // Mensajes públicos
  const [messages, setMessages] = useState([]);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState('');
  const [messagesSearch, setMessagesSearch] = useState('');
  const [messagesEnabled, setMessagesEnabled] = useState(true);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [chatEnabled, setChatEnabled] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [filesEnabled, setFilesEnabled] = useState(false);
  const [boardEnabled, setBoardEnabled] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState('');
  const [files, setFiles] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksError, setLinksError] = useState('');
  const [links, setLinks] = useState([]);
  const [linksEnabled, setLinksEnabled] = useState(true);
  const [selectedLinkResource, setSelectedLinkResource] = useState(null);
  const [linkViewerLoading, setLinkViewerLoading] = useState(false);
  const [callInfo, setCallInfo] = useState(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [accessCaptchaToken, setAccessCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [replyParent, setReplyParent] = useState(null);
  const [replyHtml, setReplyHtml] = useState('');
  const [replyName, setReplyName] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyCaptchaToken, setReplyCaptchaToken] = useState('');
  // Chat grupal
  const [chatOpen, setChatOpen] = useState(false);
  const [chatConsent, setChatConsent] = useState(false);
  const [chatDisplayName, setChatDisplayName] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatUsers, setChatUsers] = useState([]);
  const [chatConnecting, setChatConnecting] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [notificationsAllowed, setNotificationsAllowed] = useState(false);
  const [chatMuted, setChatMuted] = useState(false);
  const [callNameModalOpen, setCallNameModalOpen] = useState(false);
  const [callNameInput, setCallNameInput] = useState('');
  const [activeSection, setActiveSection] = useState('inicio');
  const connectionRef = React.useRef(null);
  const boardConnectionRef = React.useRef(null);
  const fabricInstanceRef = React.useRef(null);
  const imageInputRef = React.useRef(null);
  const isPanningRef = React.useRef(false);
  const boardPanModeRef = React.useRef(false);
  const boardSelectModeRef = React.useRef(false);
  const boardNavigationRef = React.useRef({
    mode: null,
    lastClientX: null,
    lastClientY: null,
    lastDistance: 0
  });
  const boardZoomRef = React.useRef(1);
  const boardPresenceConnRef = React.useRef(null);
  const boardPresenceNameRef = React.useRef('');
  const boardPresenceEmptyHitsRef = React.useRef(0);
  const boardActionSourceRef = React.useRef(`aula-board-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const chatScrollRef = React.useRef(null);
  const chatInputRef = React.useRef(null);
  const boardCanvasRef = React.useRef(null);
  const boardContainerRef = React.useRef(null);
  const audioCtxRef = React.useRef(null);
  const chatMutedRef = React.useRef(false);
  const callConnectionRef = React.useRef(null);
  const myBoardNameRef = React.useRef('');
  // Pizarra
  const [boardOpen, setBoardOpen] = useState(false);
  const [boardConnecting, setBoardConnecting] = useState(false);
  const [boardUsers, setBoardUsers] = useState([]);
  const [boardColor, setBoardColor] = useState('#0f172a');
  const [boardWidth, setBoardWidth] = useState(3);
  const [boardEraserWidth, setBoardEraserWidth] = useState(10);
  const [boardTextSize, setBoardTextSize] = useState(28);
  const [boardStartedBy, setBoardStartedBy] = useState('');
  const [boardStatusMsg, setBoardStatusMsg] = useState('Nadie inició la pizarra aún.');
  const [boardNameModalOpen, setBoardNameModalOpen] = useState(false);
  const [boardNameInput, setBoardNameInput] = useState('');
  const [boardEraser, setBoardEraser] = useState(false);
  const [boardExporting, setBoardExporting] = useState(false);
  const [boardPanMode, setBoardPanMode] = useState(false);
  const [boardSelectMode, setBoardSelectMode] = useState(false);
  const [boardZoom, setBoardZoom] = useState(1);
  const notificationSoundRef = React.useRef(
    typeof Audio !== 'undefined'
      ? new Audio(
          'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQQAAAAA////2/////v///7/////7/////v///7/////7/////v///7/////7/////v///7/////7/////v///7/////7/////v///7/////7/////v///7/////7/////v///7/////7/////v///7/////7/////v///7///8='
        )
      : null
  );
  const replyCaptchaRef = React.useRef(null);
  const replyCaptchaWidgetId = React.useRef(null);
  const captchaRef = React.useRef(null);
  const captchaWidgetId = React.useRef(null);
  const pageViewTrackedAulaRef = React.useRef('');
  const mainAuthSnapshot = useMemo(() => buildMainAuthSnapshot(appUser), [appUser]);
  const mainAuthRole = (mainAuthSnapshot.user?.nombreRol || '').toLowerCase();
  const isMainAppAdmin = mainAuthRole.includes('admin');
  const canUseMainAppPrivateAccess = Boolean(
    mainAuthSnapshot.token &&
    (
      mainAuthRole.includes('alumno') ||
      mainAuthRole.includes('profesor') ||
      isMainAppAdmin
    )
  );
  const activeAulaAuthToken = aulaAuthToken || (canUseMainAppPrivateAccess ? mainAuthSnapshot.token : '');
  const activeAulaAuthUser = aulaAuthUser || (canUseMainAppPrivateAccess ? mainAuthSnapshot.user : null);
  const explicitAulaPublicHint = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawValue = params.get('aulaPublica') ?? params.get('publica') ?? params.get('public');
    return parseBooleanQueryValue(rawValue);
  }, [location.search]);
  const forceAuthenticatedAccessRequested = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawValue = params.get('adminAccess') ?? params.get('authenticatedAccess') ?? params.get('authAccess');
    return parseBooleanQueryValue(rawValue) === true;
  }, [location.search]);
  const forceAuthenticatedAccess = forceAuthenticatedAccessRequested && isMainAppAdmin;
  const aulaPublicHint = explicitAulaPublicHint ?? resolvedAulaPublicHint;

  const applyAulaEstado = (nextEstado) => {
    if (!nextEstado) {
      return;
    }

    setAulaEstado(nextEstado);
    setChatEnabled(Boolean(nextEstado.permiteChatGrupalEnAula));
    setVideoEnabled(Boolean(nextEstado.permiteIniciarVideoLlamadaEnAula));
    setFilesEnabled(Boolean(nextEstado.permiteSubirArchivosEnAula));
    setBoardEnabled(Boolean(nextEstado.permitePizarraCompartidaEnAula));
    setLinksEnabled(nextEstado.permiteLinksEnAula !== false);
    setMessagesEnabled(nextEstado.permiteForoEnAula !== false);
  };

  const buildEstadoFromDetalle = (response, fallbackAulaId) => {
    const aula = response?.aula || response?.Aula || response?.aulaDetalle || response;
    if (!aula) {
      return null;
    }

    return normalizeAulaEstadoPayload(
      {
        aulaId: aula?.id || aula?.Id || fallbackAulaId,
        idColegio: aula?.idColegio || aula?.IdColegio || null,
        aulaPublica: aula?.aulaPublica ?? aula?.AulaPublica ?? false,
        idCurso: aula?.idCurso || aula?.IdCurso || null,
        nombreCurso: aula?.nombreCurso || aula?.NombreCurso || '',
        requiereAutenticacion: aula?.requiereAutenticacion ?? aula?.RequiereAutenticacion ?? false,
        idMateria: aula?.idMateria || aula?.IdMateria || null,
        nombreMateria: aula?.nombreMateria || aula?.NombreMateria || '',
        cicloLectivo: aula?.cicloLectivo || aula?.CicloLectivo || null,
        permiteChatGrupalEnAula: response?.permiteChatGrupalEnAula ?? response?.PermiteChatGrupalEnAula ?? false,
        permiteIniciarVideoLlamadaEnAula:
          response?.permiteIniciarVideoLlamadaEnAula ?? response?.PermiteIniciarVideoLlamadaEnAula ?? false,
        permiteSubirArchivosEnAula:
          response?.permiteSubirArchivosEnAula ??
          response?.PermiteSubirArchivosEnAula ??
          response?.permiteSubirArchivosAlAula ??
          response?.PermiteSubirArchivosAlAula ??
          false,
        permitePizarraCompartidaEnAula:
          response?.permitePizarraCompartidaEnAula ?? response?.PermitePizarraCompartidaEnAula ?? false,
        permiteLinksEnAula: response?.permiteLinksEnAula ?? response?.PermiteLinksEnAula ?? true,
        permiteForoEnAula:
          response?.permiteForoEnAula ??
          response?.PermiteForoEnAula ??
          response?.permiteEnviarMensajesEnAula ??
          response?.PermiteEnviarMensajesEnAula ??
          true,
        mostrarAsistentesIA:
          response?.mostrarAsistentesIA ??
          response?.MostrarAsistentesIA ??
          aula?.mostrarAsistentesIA ??
          aula?.MostrarAsistentesIA ??
          true,
        permiteChatsOpenAI: response?.permiteChatsOpenAI ?? response?.PermiteChatsOpenAI ?? false,
        permiteAgentesClasicosGPT: response?.permiteAgentesClasicosGPT ?? response?.PermiteAgentesClasicosGPT ?? false
      },
      fallbackAulaId
    );
  };

  useEffect(() => {
    return () => {
      const instance = fabricInstanceRef.current;
      if (instance) {
        if (typeof instance.__boardTouchCleanup === 'function') {
          instance.__boardTouchCleanup();
        }
        instance.dispose();
        fabricInstanceRef.current = null;
      }
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
      }
      if (callConnectionRef.current) {
        callConnectionRef.current.stop();
        callConnectionRef.current = null;
      }
      if (boardConnectionRef.current) {
        boardConnectionRef.current.stop();
        boardConnectionRef.current = null;
      }
      if (boardPresenceConnRef.current) {
        boardPresenceConnRef.current.stop();
        boardPresenceConnRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    scrollChatToBottom();
  }, [chatMessages]);

  useEffect(() => {
    if (!boardOpen) return;
    const resize = () => {
      const canvasInstance = fabricInstanceRef.current;
      const container = boardContainerRef.current;
      if (!canvasInstance || !container) return;
      const { width, height } = container.getBoundingClientRect();
      // La sección oculta mide 0: se recalcula al volver a mostrarla.
      if (!width) return;
      const targetHeight = Math.max(height, window.innerWidth < 768 ? 420 : 560);
      canvasInstance.setWidth(width);
      canvasInstance.setHeight(targetHeight);
      canvasInstance.renderAll();
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [activeSection, boardOpen]);

  useEffect(() => {
    const canvasInstance = fabricInstanceRef.current;
    if (!canvasInstance) return;
    boardPanModeRef.current = boardPanMode;
    boardSelectModeRef.current = boardSelectMode;
    canvasInstance.isDrawingMode = !boardPanMode && !boardSelectMode;
    canvasInstance.selection = boardSelectMode;
    const brush = canvasInstance.freeDrawingBrush;
    if (brush) {
      brush.color = boardEraser ? '#ffffff' : boardColor;
      brush.width = boardEraser ? boardEraserWidth : boardWidth;
    }
    canvasInstance.forEachObject((o) => {
      applyBoardObjectInteractivity(o, boardSelectMode);
    });
    if (!boardSelectMode && canvasInstance.getActiveObject()) {
      canvasInstance.discardActiveObject();
      canvasInstance.requestRenderAll();
    }
  }, [boardColor, boardWidth, boardEraserWidth, boardEraser, boardPanMode, boardSelectMode]);

  // Asegura canvas listo al abrir la pizarra
  useEffect(() => {
    if (boardOpen) {
      initBoardCanvas();
    } else {
      disposeBoardCanvas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardOpen]);

  useEffect(() => {
    chatMutedRef.current = chatMuted;
  }, [chatMuted]);

  // Presencia en tiempo real de la pizarra (sin unirse): sonda periódica vía SignalR
  useEffect(() => {
    const requiresPrivateAccess = Boolean(aulaEstado && !aulaEstado.aulaPublica && aulaEstado.requiereAutenticacion);
    const privateAccessReady = Boolean(requiresPrivateAccess && activeAulaAuthToken && activeAulaAuthUser);
    if (!boardEnabled || boardOpen || boardConnecting || !aulaEstado?.aulaId) return;
    if (requiresPrivateAccess && !privateAccessReady) return;

    let intervalId;
    let stopped = false;

    const startPresence = async () => {
      try {
        const hubBase =
          resolveHubBaseUrl();

        const conn = new HubConnectionBuilder()
          .withUrl(`${hubBase}/hubs/aula-board`, {
            withCredentials: true,
            accessTokenFactory: () => (requiresPrivateAccess ? activeAulaAuthToken || '' : '')
          })
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Warning)
          .build();

        await conn.start();
        boardPresenceConnRef.current = conn;
        const observerName = `__observer__${Math.random().toString(36).slice(2, 7)}`;
        boardPresenceNameRef.current = observerName;
        try {
          await conn.invoke('JoinBoard', aulaEstado.aulaId, observerName);
        } catch (_) {
          /* ignore join failures */
        }

        const poll = async () => {
          if (stopped || !boardPresenceConnRef.current || boardOpen) return;
          try {
            const users = await boardPresenceConnRef.current.invoke('GetConnectedUsers', aulaEstado.aulaId);
            if (Array.isArray(users)) {
              const cleaned = cleanBoardUsers(users);
              setBoardUsers(cleaned);
              if (cleaned.length > 0) {
                boardPresenceEmptyHitsRef.current = 0;
                setBoardStartedBy(cleaned[0]);
                setBoardStatusMsg(`${cleaned[0]} inició una pizarra colaborativa (en curso). Unite para participar.`);
              } else {
                boardPresenceEmptyHitsRef.current += 1;
                // Solo limpiar estado si vemos vacío dos veces seguidas
                if (boardPresenceEmptyHitsRef.current >= 2) {
                  setBoardStartedBy('');
                  setBoardStatusMsg('Nadie inició la pizarra aún.');
                }
              }
            }
          } catch (_) { /* silencioso */ }
        };

        await poll(); // primer chequeo inmediato
        intervalId = window.setInterval(poll, 5000);
      } catch (_) {
        boardPresenceConnRef.current = null;
      }
    };

    startPresence();

    return () => {
      stopped = true;
      if (intervalId) clearInterval(intervalId);
      if (boardPresenceConnRef.current) {
        boardPresenceConnRef.current.stop().catch(() => {});
        boardPresenceConnRef.current = null;
      }
      boardPresenceEmptyHitsRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardEnabled, aulaEstado?.aulaId, aulaEstado?.aulaPublica, aulaEstado?.requiereAutenticacion, boardOpen, boardConnecting, activeAulaAuthToken, activeAulaAuthUser]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idParam = params.get('id') || params.get('aulaId') || '';
    const colegioParam = params.get('colegio') || params.get('regional') || '';
    const aulaParam = params.get('aula') || params.get('asistente') || params.get('nickname') || '';
    if (!idParam && colegioParam && aulaParam) {
      setAulaId('');
      setResolvedAulaPublicHint(null);
      setEstadoError('');
      setLoadingEstado(true);
      AulaService.getAulaPublicaPorNickname(colegioParam, aulaParam)
        .then((response) => {
          if (response?.ok === false) {
            throw new Error(response?.error || 'No pudimos encontrar el aula.');
          }
          const resolvedId = response?.aulaId || response?.AulaId;
          if (!resolvedId) {
            throw new Error('No pudimos encontrar el aula.');
          }
          setResolvedAulaPublicHint(parseBooleanQueryValue(response?.aulaPublica ?? response?.AulaPublica));
          setAulaId(resolvedId);
        })
        .catch((error) => {
          setEstadoError(error.message || 'No pudimos encontrar el aula.');
        })
        .finally(() => setLoadingEstado(false));
      return;
    }
    setResolvedAulaPublicHint(null);
    setAulaId(idParam || '');
  }, [location.search]);

  useEffect(() => {
    if (!aulaId) return;
    const fetchEstado = async () => {
      setLoadingEstado(true);
      setEstadoError('');
      setAulaEstado(null);
      setAulaDetalle(null);
      setClassicAssistants([]);
      setOpenAIAssistants([]);
      setSelectedAssistant(null);
      setFiles([]);
      setFilesError('');
      setLinks([]);
      setLinksError('');
      setSelectedLinkResource(null);
      try {
        if (forceAuthenticatedAccess || aulaPublicHint === false) {
          if (!activeAulaAuthToken || !activeAulaAuthUser) {
            if (forceAuthenticatedAccess) {
              navigate('/login', {
                replace: true,
                state: {
                  from: `${location.pathname}${location.search}`
                }
              });
              return;
            }
            throw new Error('Esta aula es privada. Debes iniciar sesión como alumno para ingresar.');
          }

          await loadPrivateDetalle(aulaId, activeAulaAuthToken, activeAulaAuthUser);
          return;
        }

        if (!accessCaptchaToken) {
          return;
        }

        const response = await AulaService.getAulaPublicaEstado(aulaId);
        if (response?.ok === false) {
          throw new Error(response?.error || 'No pudimos obtener el estado del aula.');
        }

        const normalizedEstado = normalizeAulaEstadoPayload(response, aulaId);
        applyAulaEstado(normalizedEstado);

        if (normalizedEstado.aulaPublica) {
          if (accessCaptchaToken) {
            await loadPublicDetalle(aulaId);
          }
          return;
        }

        if (normalizedEstado.requiereAutenticacion) {
          if (!activeAulaAuthToken || !activeAulaAuthUser) {
            navigate('/login', {
              replace: true,
              state: {
                from: `${location.pathname}${location.search}`
              }
            });
            return;
          }

          await loadPrivateDetalle(aulaId, activeAulaAuthToken, activeAulaAuthUser);
        }
      } catch (error) {
        if ((error.message || '').toLowerCase().includes('sesión')) {
          clearAulaSession();
        }
        setEstadoError(error.message || 'No pudimos cargar el aula.');
      } finally {
        setLoadingEstado(false);
      }
    };

    fetchEstado();
  }, [aulaId, accessCaptchaToken, activeAulaAuthToken, activeAulaAuthUser, aulaPublicHint, forceAuthenticatedAccess, navigate, location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPublicDetalle = async (id) => {
    setLoadingDetalle(true);
    setEstadoError('');
    try {
      const response = await AulaService.getAulaPublicaDetalle(id);
      if (response?.ok === false) {
        throw new Error(response?.error || 'No pudimos cargar el aula.');
      }
      const aula = response?.aula || response?.Aula || response?.aulaDetalle || response;
      const { classicAssistants: klassics, openAIAssistants: openAI } = normalizeAssistantCollections(response);
      const normalizedEstado = buildEstadoFromDetalle(response, id);
      const assistantsVisible = normalizedEstado?.mostrarAsistentesIA !== false;
      setAulaDetalle(aula);
      setClassicAssistants(assistantsVisible ? klassics : []);
      setOpenAIAssistants(assistantsVisible ? openAI : []);
      applyAulaEstado(normalizedEstado);
      if (accessCaptchaToken) {
        void loadMessages(accessCaptchaToken, messagesSearch);
      }
    } catch (error) {
      setEstadoError(error.message || 'No pudimos cargar el aula.');
    } finally {
      setLoadingDetalle(false);
    }
  };

  const loadPrivateDetalle = async (id, tokenParam = activeAulaAuthToken, userParam = activeAulaAuthUser) => {
    const token = tokenParam || activeAulaAuthToken;
    if (!token) {
      setEstadoError('Esta aula es privada. Debes iniciar sesión como alumno para ingresar.');
      return;
    }

    setLoadingDetalle(true);
    setEstadoError('');
    try {
      const response = await AulaService.getAulaAccessDetalle(id, token);
      const aula = response?.aula || response?.Aula || response?.aulaDetalle || response;
      const { classicAssistants: klassics, openAIAssistants: openAI } = normalizeAssistantCollections(response);
      const normalizedEstado = buildEstadoFromDetalle(response, id);
      const assistantsVisible = normalizedEstado?.mostrarAsistentesIA !== false;
      setAulaDetalle(aula);
      setClassicAssistants(assistantsVisible ? klassics : []);
      setOpenAIAssistants(assistantsVisible ? openAI : []);
      applyAulaEstado(normalizedEstado);
      const resolvedUser = userParam || aulaSessionService.getUser() || activeAulaAuthUser;
      if (resolvedUser) {
        const fullName = `${resolvedUser?.nombre || ''} ${resolvedUser?.apellido || ''}`.trim() ||
          resolvedUser?.displayName ||
          resolvedUser?.email ||
          '';
        setChatDisplayName(fullName);
        setReplyName(fullName);
        setCallNameInput(fullName);
        setBoardNameInput(fullName);
      }
    } catch (error) {
      clearAulaSession();
      setAulaDetalle(null);
      const message = error.message || 'No pudimos cargar el aula privada.';
      if (message.toLowerCase().includes('sesión') || message.toLowerCase().includes('401')) {
        navigate('/login', {
          replace: true,
          state: {
            from: `${location.pathname}${location.search}`
          }
        });
        return;
      }
      setEstadoError(message);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const formatAulaNombre = (name) => {
    if (!name) return 'Aula';
    // Inserta espacio entre minúscula+mayúscula y reemplaza guiones/underscores por espacios
    const withSpaces = name
      .replace(/([a-záéíóúüñ])([A-ZÁÉÍÓÚÜÑ])/g, '$1 $2')
      .replace(/[_-]+/g, ' ');
    return withSpaces.replace(/\s+/g, ' ').trim();
  };

  const aulaNombre = useMemo(() => formatAulaNombre(aulaDetalle?.nombre || aulaDetalle?.Nombre || 'Aula'), [aulaDetalle]);
  const aulaDescripcion = aulaDetalle?.descripcion || aulaDetalle?.Descripcion || '';
  const aulaProfesor = aulaDetalle?.nombreProfesor || aulaDetalle?.NombreProfesor || 'Profesor';
  const aulaMateria = aulaDetalle?.nombreMateria || aulaDetalle?.NombreMateria || '';
  const aulaCiclo = aulaDetalle?.cicloLectivo || aulaDetalle?.CicloLectivo || '';
  const aulaColegio = aulaDetalle?.nombreColegio || aulaDetalle?.NombreColegio || '';
  const jitsiBase = process.env.REACT_APP_JITSI_BASE || 'https://meet.jit.si';

  const colegioId = aulaDetalle?.idColegio || aulaDetalle?.IdColegio || aulaEstado?.idColegio;
  const archivosDisponibles = files?.length || 0;
  const linksDisponibles = links?.length || 0;
  const assistants = useMemo(() => [...classicAssistants, ...openAIAssistants], [classicAssistants, openAIAssistants]);
  const assistantsVisible = aulaEstado?.mostrarAsistentesIA !== false;
  const classicAgentsEnabled = assistantsVisible && (aulaEstado?.permiteAgentesClasicosGPT ?? false);
  const openAIChatsEnabled = assistantsVisible && (aulaEstado?.permiteChatsOpenAI ?? false);
  const showAssistantsSection = classicAgentsEnabled || openAIChatsEnabled;
  const selectedAssistantId = getAssistantId(selectedAssistant);
  const requiresPrivateAuthentication = Boolean(
    aulaEstado &&
    !aulaEstado.aulaPublica &&
    (aulaEstado.requiereAutenticacion ?? aulaEstado.RequiereAutenticacion)
  );
  const aulaAuthFullName = useMemo(() => {
    const nombre = `${activeAulaAuthUser?.nombre || ''} ${activeAulaAuthUser?.apellido || ''}`.trim();
    return nombre || activeAulaAuthUser?.displayName || activeAulaAuthUser?.email || '';
  }, [activeAulaAuthUser]);
  const participantDisplayName = requiresPrivateAuthentication
    ? aulaAuthFullName
    : (chatDisplayName || '').trim();
  const isPrivateAuthenticated = Boolean((requiresPrivateAuthentication || forceAuthenticatedAccess) && activeAulaAuthToken && activeAulaAuthUser);
  const shouldUsePublicFlow = !forceAuthenticatedAccess && (aulaPublicHint === true || Boolean(aulaEstado?.aulaPublica));
  const requiresAccessCaptcha = !forceAuthenticatedAccess && aulaPublicHint !== false && !(aulaEstado && aulaEstado.aulaPublica === false);
  // Presencia sin unirse: permite ver que el chat ya está en vivo (la pizarra tiene su propia sonda).
  const chatPresenceUsers = useAulaHubPresence({
    enabled: chatEnabled && !chatOpen && !chatConnecting && (isPrivateAuthenticated || shouldUsePublicFlow),
    hubUrl: `${(
      resolveHubBaseUrl()
    )}/hubs/aula-chat`,
    aulaId: aulaEstado?.aulaId,
    accessTokenFactory: () => (isPrivateAuthenticated ? activeAulaAuthToken || '' : '')
  });
  const chatLive = chatOpen || chatPresenceUsers.length > 0;

  const clearAulaSession = () => {
    if (connectionRef.current) {
      connectionRef.current.stop().catch(() => {});
      connectionRef.current = null;
    }
    if (boardConnectionRef.current) {
      boardConnectionRef.current.stop().catch(() => {});
      boardConnectionRef.current = null;
    }
    if (callConnectionRef.current) {
      callConnectionRef.current.stop().catch(() => {});
      callConnectionRef.current = null;
    }
    aulaSessionService.clearSession();
    setAulaAuthToken('');
    setAulaAuthUser(null);
    setChatOpen(false);
    setChatMessages([]);
    setChatUsers([]);
    setBoardOpen(false);
    setBoardUsers([]);
    setBoardStartedBy('');
    setBoardStatusMsg('Nadie inició la pizarra aún.');
    disposeBoardCanvas();
    setCallInfo(null);
  };

  const emitTelemetry = (tipoEvento, overrides = {}) => {
    const resolvedAulaId = aulaEstado?.aulaId || aulaId;
    if (!resolvedAulaId) return;

    const payload = {
      tipoEvento,
      sessionId: getOrCreateAulaTelemetrySessionId(),
      aulaNombre,
      ruta: getCurrentPublicRoute(),
      ...overrides
    };

    if (isPrivateAuthenticated) {
      void AulaService.trackAuthenticatedTelemetryEvent(resolvedAulaId, payload, activeAulaAuthToken).catch(() => {});
      return;
    }

    void AulaService.trackPublicTelemetryEvent({
      aulaId: resolvedAulaId,
      ...payload
    }).catch(() => {});
  };

  const scrollChatToBottom = () => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  };

  const cleanBoardUsers = (list) =>
    Array.isArray(list) ? list.filter((u) => typeof u === 'string' && !u.startsWith('__observer__')) : [];

  const ensureNotificationPermission = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      setNotificationsAllowed(false);
      return false;
    }
    if (Notification.permission === 'granted') {
      setNotificationsAllowed(true);
      return true;
    }
    if (Notification.permission === 'denied') {
      setNotificationsAllowed(false);
      return false;
    }
    const result = await Notification.requestPermission();
    const granted = result === 'granted';
    setNotificationsAllowed(granted);
    return granted;
  };

  useEffect(() => {
    const resolvedAulaId = aulaEstado?.aulaId || aulaId;
    if (!resolvedAulaId || !aulaDetalle) return;
    if (pageViewTrackedAulaRef.current === resolvedAulaId) return;

    pageViewTrackedAulaRef.current = resolvedAulaId;
    emitTelemetry('aula.page_view', {
      origen: isPrivateAuthenticated ? 'aula_privada' : 'aula_publica'
    });
  }, [aulaDetalle, aulaEstado?.aulaId, aulaId, aulaNombre]);

  const formatDateTime = (dateValue) => {
    const d = new Date(dateValue || Date.now());
    const pad = (n) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const normalizeChatExportText = (value) => (
    String(value ?? '')
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\n')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
  );

  // ---------- PIZARRA ----------
  const clampBoardZoom = (zoom) => Math.min(Math.max(zoom, BOARD_MIN_ZOOM), BOARD_MAX_ZOOM);

  const resetBoardNavigationState = () => {
    boardNavigationRef.current = {
      mode: null,
      lastClientX: null,
      lastClientY: null,
      lastDistance: 0
    };
    isPanningRef.current = false;
  };

  const syncBoardZoom = (instance) => {
    if (!instance) return;
    const nextZoom = Math.round(clampBoardZoom(instance.getZoom()) * 100) / 100;
    if (boardZoomRef.current !== nextZoom) {
      boardZoomRef.current = nextZoom;
      setBoardZoom(nextZoom);
    }
  };

  const setBoardInteractionMode = (instance) => {
    if (!instance) return;
    instance.isDrawingMode = !boardPanModeRef.current && !boardSelectModeRef.current;
    instance.selection = boardSelectModeRef.current;
  };

  const panBoardViewport = (instance, dx, dy) => {
    if (!instance || (!dx && !dy)) return;
    const vpt = instance.viewportTransform ? [...instance.viewportTransform] : [1, 0, 0, 1, 0, 0];
    vpt[4] += dx;
    vpt[5] += dy;
    instance.setViewportTransform(vpt);
    instance.requestRenderAll();
  };

  const zoomBoardToPoint = (instance, nextZoom, point) => {
    if (!instance || !point) return;
    instance.zoomToPoint(point, clampBoardZoom(nextZoom));
    syncBoardZoom(instance);
    instance.requestRenderAll();
  };

  const zoomBoardByFactor = (factor) => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    const center = new fabric.Point(instance.getWidth() / 2, instance.getHeight() / 2);
    zoomBoardToPoint(instance, instance.getZoom() * factor, center);
  };

  const getEventClientPoint = (event) => {
    if (!event) return null;
    if (event.touches?.length) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    if (event.changedTouches?.length) {
      return { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
    }
    if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
      return { x: event.clientX, y: event.clientY };
    }
    return null;
  };

  const getTouchDistance = (touches) => {
    if (!touches || touches.length < 2) return 0;
    return Math.hypot(touches[1].clientX - touches[0].clientX, touches[1].clientY - touches[0].clientY);
  };

  const getTouchCenter = (touches) => {
    if (!touches || touches.length < 2) return null;
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    };
  };

  const isStylusTouch = (touch) => touch?.touchType === 'stylus';

  const disposeBoardCanvas = () => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    if (typeof instance.__boardTouchCleanup === 'function') {
      instance.__boardTouchCleanup();
    }
    instance.dispose();
    fabricInstanceRef.current = null;
    boardZoomRef.current = 1;
    resetBoardNavigationState();
  };

  const ensureCanvasReady = async () => {
    if (!fabricInstanceRef.current) {
      await initBoardCanvas();
    }
    return fabricInstanceRef.current;
  };

  const initBoardCanvas = async () => {
    const canvasEl = boardCanvasRef.current;
    const container = boardContainerRef.current;
    if (!canvasEl || !container) return;

    if (fabricInstanceRef.current) {
      disposeBoardCanvas();
    }

    const instance = new fabric.Canvas(canvasEl, {
      isDrawingMode: !boardPanMode,
      selection: false,
      backgroundColor: '#ffffff'
    });
    const { width, height } = container.getBoundingClientRect();
    const targetHeight = Math.max(height, window.innerWidth < 768 ? 420 : 560);
    instance.setWidth(width);
    instance.setHeight(targetHeight);
    // bloquear menú contextual en ambas capas
    canvasEl.oncontextmenu = (e) => e.preventDefault();
    instance.upperCanvasEl.oncontextmenu = (e) => e.preventDefault();
    if (instance.freeDrawingBrush) {
      instance.freeDrawingBrush.color = boardEraser ? '#ffffff' : boardColor;
      instance.freeDrawingBrush.width = boardEraser ? boardEraserWidth : boardWidth;
    }
    instance.upperCanvasEl.style.touchAction = 'none';
    // bloquear menú contextual del browser en el canvas
    instance.upperCanvasEl.oncontextmenu = (e) => e.preventDefault();
    boardZoomRef.current = 1;
    setBoardZoom(1);
    resetBoardNavigationState();

    // Pan (clic derecho o modo mano)
    instance.on('mouse:down', (opt) => {
      const e = opt.e;
      if (e?.type?.startsWith('touch')) return;
      const pan = boardPanModeRef.current || e.button === 2 || e.altKey || e.metaKey;
      if (pan) {
        isPanningRef.current = true;
        instance.isDrawingMode = false;
        const point = getEventClientPoint(e);
        boardNavigationRef.current = {
          mode: 'mouse-pan',
          lastClientX: point?.x ?? null,
          lastClientY: point?.y ?? null,
          lastDistance: 0
        };
      } else if (!boardSelectModeRef.current) {
        instance.isDrawingMode = true;
      } else {
        instance.isDrawingMode = false;
      }
    });

    // Broadcast de cambios en objetos (ej. mover/escala de imagen)
    const sendObjectUpdate = (target) => {
      if (!target) return;
      const json = getBoardObjectJson(target);
      sendBoardAction({ type: 'fabric-path', object: json });
      const snap = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
      snap.background = instance.backgroundColor || '#ffffff';
      sendBoardAction({ type: 'snapshot', snapshot: snap });
    };

    instance.on('object:modified', (e) => {
      if (!boardSelectModeRef.current) return;
      sendObjectUpdate(e.target);
    });

    instance.on('object:moving', (e) => {
      // throttle ligero podría añadirse si hace falta
      if (!boardSelectModeRef.current) return;
      sendObjectUpdate(e.target);
    });

    instance.on('text:changed', (e) => {
      sendObjectUpdate(e.target);
    });

    instance.on('mouse:move', (opt) => {
      if (opt.e?.type?.startsWith('touch')) return;
      if (!isPanningRef.current) return;
      const e = opt.e;
      const point = getEventClientPoint(e);
      let dx = e.movementX || 0;
      let dy = e.movementY || 0;

      if ((!dx && !dy) && point && boardNavigationRef.current.lastClientX !== null) {
        dx = point.x - boardNavigationRef.current.lastClientX;
        dy = point.y - boardNavigationRef.current.lastClientY;
      }

      if (point) {
        boardNavigationRef.current.lastClientX = point.x;
        boardNavigationRef.current.lastClientY = point.y;
      }

      panBoardViewport(instance, dx, dy);
    });

    instance.on('mouse:up', (opt) => {
      if (opt?.e?.type?.startsWith('touch')) return;
      setBoardInteractionMode(instance);
      resetBoardNavigationState();
    });

    // Zoom con rueda o trackpad
    instance.on('mouse:wheel', (opt) => {
      const delta = opt.e.deltaY;
      let zoom = instance.getZoom();
      zoom *= 0.999 ** delta;
      const point = new fabric.Point(opt.e.offsetX, opt.e.offsetY);
      zoomBoardToPoint(instance, zoom, point);
      opt.e.preventDefault();
      opt.e.stopPropagation();
    });

    const upperCanvasEl = instance.upperCanvasEl;
    const handleTouchStart = (event) => {
      const touches = Array.from(event.touches || []);
      if (touches.length === 2) {
        const center = getTouchCenter(touches);
        boardNavigationRef.current = {
          mode: 'pinch',
          lastClientX: center?.x ?? null,
          lastClientY: center?.y ?? null,
          lastDistance: getTouchDistance(touches)
        };
        instance.isDrawingMode = false;
        event.preventDefault();
        return;
      }

      if (touches.length === 1 && boardPanModeRef.current && !isStylusTouch(touches[0])) {
        boardNavigationRef.current = {
          mode: 'touch-pan',
          lastClientX: touches[0].clientX,
          lastClientY: touches[0].clientY,
          lastDistance: 0
        };
        instance.isDrawingMode = false;
        event.preventDefault();
      }
    };

    const handleTouchMove = (event) => {
      const touches = Array.from(event.touches || []);

      if (touches.length === 2) {
        const center = getTouchCenter(touches);
        const distance = getTouchDistance(touches);
        if (!center || !distance) return;

        if (boardNavigationRef.current.mode !== 'pinch') {
          boardNavigationRef.current = {
            mode: 'pinch',
            lastClientX: center.x,
            lastClientY: center.y,
            lastDistance: distance
          };
        }

        const dx = center.x - (boardNavigationRef.current.lastClientX ?? center.x);
        const dy = center.y - (boardNavigationRef.current.lastClientY ?? center.y);
        panBoardViewport(instance, dx, dy);

        const previousDistance = boardNavigationRef.current.lastDistance || distance;
        const rect = upperCanvasEl.getBoundingClientRect();
        const point = new fabric.Point(center.x - rect.left, center.y - rect.top);
        zoomBoardToPoint(instance, instance.getZoom() * (distance / previousDistance), point);

        boardNavigationRef.current = {
          mode: 'pinch',
          lastClientX: center.x,
          lastClientY: center.y,
          lastDistance: distance
        };
        event.preventDefault();
        return;
      }

      if (touches.length === 1 && boardNavigationRef.current.mode === 'gesture-hold') {
        event.preventDefault();
        return;
      }

      if (touches.length === 1 && boardPanModeRef.current && boardNavigationRef.current.mode === 'touch-pan' && !isStylusTouch(touches[0])) {
        const touch = touches[0];
        const dx = touch.clientX - (boardNavigationRef.current.lastClientX ?? touch.clientX);
        const dy = touch.clientY - (boardNavigationRef.current.lastClientY ?? touch.clientY);
        boardNavigationRef.current.lastClientX = touch.clientX;
        boardNavigationRef.current.lastClientY = touch.clientY;
        panBoardViewport(instance, dx, dy);
        event.preventDefault();
      }
    };

    const handleTouchEnd = (event) => {
      const touches = Array.from(event.touches || []);

      if (touches.length === 2) {
        const center = getTouchCenter(touches);
        boardNavigationRef.current = {
          mode: 'pinch',
          lastClientX: center?.x ?? null,
          lastClientY: center?.y ?? null,
          lastDistance: getTouchDistance(touches)
        };
        event.preventDefault();
        return;
      }

      if (touches.length === 1 && boardPanModeRef.current && !isStylusTouch(touches[0])) {
        boardNavigationRef.current = {
          mode: 'touch-pan',
          lastClientX: touches[0].clientX,
          lastClientY: touches[0].clientY,
          lastDistance: 0
        };
        instance.isDrawingMode = false;
        event.preventDefault();
        return;
      }

      if (touches.length === 1 && boardNavigationRef.current.mode === 'pinch') {
        boardNavigationRef.current = {
          mode: 'gesture-hold',
          lastClientX: touches[0].clientX,
          lastClientY: touches[0].clientY,
          lastDistance: 0
        };
        instance.isDrawingMode = false;
        event.preventDefault();
        return;
      }

      setBoardInteractionMode(instance);
      resetBoardNavigationState();
    };

    upperCanvasEl.addEventListener('touchstart', handleTouchStart, { passive: false });
    upperCanvasEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    upperCanvasEl.addEventListener('touchend', handleTouchEnd, { passive: false });
    upperCanvasEl.addEventListener('touchcancel', handleTouchEnd, { passive: false });
    instance.__boardTouchCleanup = () => {
      upperCanvasEl.removeEventListener('touchstart', handleTouchStart);
      upperCanvasEl.removeEventListener('touchmove', handleTouchMove);
      upperCanvasEl.removeEventListener('touchend', handleTouchEnd);
      upperCanvasEl.removeEventListener('touchcancel', handleTouchEnd);
    };

    instance.on('path:created', (evt) => {
      if (boardSelectModeRef.current) {
        // si estaba en modo edición, ignoramos trazos accidentales
        instance.remove(evt.path);
        return;
      }
      const obj = evt.path;
      if (!obj) return;
      obj.selectable = false;
      const payload = {
        type: 'fabric-path',
        object: obj.toJSON(['path', 'stroke', 'strokeWidth', 'fill', 'strokeLineCap', 'strokeLineJoin'])
      };
      sendBoardAction(payload);
    });

    fabricInstanceRef.current = instance;
    const size = container.getBoundingClientRect();
    instance.setWidth(size.width);
    instance.setHeight(Math.max(size.height, window.innerWidth < 768 ? 420 : 560));
    syncBoardZoom(instance);
    setBoardInteractionMode(instance);
    instance.renderAll();
  };

  const clearBoardLocal = () => {
    if (!fabricInstanceRef.current) return;
    fabricInstanceRef.current.clear();
    fabricInstanceRef.current.backgroundColor = '#ffffff';
    fabricInstanceRef.current.renderAll();
  };

  const resetBoardView = () => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    instance.setViewportTransform([1, 0, 0, 1, 0, 0]);
    instance.setZoom(1);
    syncBoardZoom(instance);
    instance.renderAll();
  };

  const addImageToBoard = (dataUrl) => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    fabric.Image.fromURL(dataUrl, (img) => {
      const cw = instance.getWidth();
      const ch = instance.getHeight();
      const scale = Math.min((cw * 0.7) / img.width, (ch * 0.7) / img.height, 1);
      img.set({
        left: cw * 0.15,
        top: ch * 0.15,
        scaleX: scale,
        scaleY: scale,
        selectable: true,
        evented: true
      });
      instance.add(img);
      instance.setActiveObject(img);
      instance.renderAll();
      // Enviamos el objeto puntual para velocidad + snapshot por si alguien se suma tarde
      const imgPayload = getBoardObjectJson(img);
      sendBoardAction({ type: 'fabric-path', object: imgPayload });
      const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
      snapshot.background = instance.backgroundColor || '#ffffff';
      sendBoardAction({ type: 'snapshot', snapshot });
    });
  };

  const addTextToBoard = () => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;

    const cw = instance.getWidth();
    const ch = instance.getHeight();
    const text = new fabric.IText('Escribí texto', {
      left: cw * 0.18,
      top: ch * 0.18,
      width: Math.min(360, cw * 0.62),
      fill: boardColor,
      fontSize: boardTextSize,
      fontFamily: 'Inter, Manrope, Arial, sans-serif',
      fontWeight: '600',
      editable: true,
      selectable: true,
      evented: true
    });

    boardPanModeRef.current = false;
    boardSelectModeRef.current = true;
    setBoardEraser(false);
    setBoardPanMode(false);
    setBoardSelectMode(true);
    instance.isDrawingMode = false;
    instance.selection = true;
    instance.add(text);
    instance.setActiveObject(text);
    instance.requestRenderAll();

    sendBoardAction({ type: 'fabric-path', object: getBoardObjectJson(text) });
    const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
    snapshot.background = instance.backgroundColor || '#ffffff';
    sendBoardAction({ type: 'snapshot', snapshot });

    window.setTimeout(() => {
      text.enterEditing();
      text.selectAll();
      text.hiddenTextarea?.focus();
    }, 0);
  };

  const sendBoardAction = async (action) => {
    if (!boardConnectionRef.current || !aulaEstado?.aulaId) return;
    try {
      await boardConnectionRef.current.invoke(
        'SendBoardAction',
        aulaEstado.aulaId,
        (participantDisplayName || 'Invitado').trim(),
        JSON.stringify({ ...action, sourceId: boardActionSourceRef.current })
      );
    } catch (_) {
      /* ignore */
    }
  };

  const handleBoardColorChange = (event) => {
    const nextColor = event.target.value;
    setBoardColor(nextColor);

    const instance = fabricInstanceRef.current;
    if (!instance) return;

    const selectedTextObjects = getBoardSelectionObjects(instance.getActiveObject()).filter(isBoardTextObject);
    if (selectedTextObjects.length === 0) return;

    selectedTextObjects.forEach((object) => {
      object.set('fill', nextColor);
      sendBoardAction({ type: 'fabric-path', object: getBoardObjectJson(object) });
    });
    instance.requestRenderAll();

    const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
    snapshot.background = instance.backgroundColor || '#ffffff';
    sendBoardAction({ type: 'snapshot', snapshot });
  };

  const handleBoardTextSizeChange = (event) => {
    const nextSize = Number(event.target.value);
    setBoardTextSize(nextSize);

    const instance = fabricInstanceRef.current;
    if (!instance) return;

    const selectedTextObjects = getBoardSelectionObjects(instance.getActiveObject()).filter(isBoardTextObject);
    if (selectedTextObjects.length === 0) return;

    selectedTextObjects.forEach((object) => {
      object.set('fontSize', nextSize);
      if (typeof object.initDimensions === 'function') {
        object.initDimensions();
      }
      object.setCoords();
      sendBoardAction({ type: 'fabric-path', object: getBoardObjectJson(object) });
    });
    instance.requestRenderAll();

    const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
    snapshot.background = instance.backgroundColor || '#ffffff';
    sendBoardAction({ type: 'snapshot', snapshot });
  };

  const handleRemoteFabricObject = async (data) => {
    if (!fabricInstanceRef.current) {
      await initBoardCanvas();
    }
    if (!fabricInstanceRef.current) return;

    if (data.type === 'snapshot' && data.snapshot) {
      fabricInstanceRef.current.loadFromJSON(data.snapshot, () => {
        fabricInstanceRef.current.forEachObject((o) => {
          applyBoardObjectInteractivity(o, boardSelectModeRef.current);
        });
        if (data.snapshot.background) {
          fabricInstanceRef.current.backgroundColor = data.snapshot.background;
        }
        fabricInstanceRef.current.renderAll();
      });
      return;
    }

    if (data.object && data.object.type === 'image' && data.object.src) {
      const instance = fabricInstanceRef.current;
      fabric.Image.fromURL(
        data.object.src,
        (img) => {
          img.set({
            left: data.object.left || 0,
            top: data.object.top || 0,
            scaleX: data.object.scaleX || 1,
            scaleY: data.object.scaleY || 1,
            angle: data.object.angle || 0,
            selectable: false,
            evented: false
          });
          applyBoardObjectInteractivity(img, boardSelectModeRef.current);
          instance.add(img);
          instance.renderAll();
        },
        { crossOrigin: 'anonymous' }
      );
      return;
    }

    if (data.object) {
      fabric.util.enlivenObjects([data.object], (objects) => {
        objects.forEach((o) => {
          applyBoardObjectInteractivity(o, boardSelectModeRef.current);
          fabricInstanceRef.current.add(o);
        });
        fabricInstanceRef.current.renderAll();
      });
      return;
    }

    if (data.path && Array.isArray(data.path)) {
      const pathString = data.path.reduce((acc, p, idx) => (idx === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`), '');
      const pathObj = new fabric.Path(pathString, {
        stroke: data.color || '#0f172a',
        strokeWidth: data.width || 3,
        fill: '',
        strokeLineCap: 'round',
        strokeLineJoin: 'round',
        selectable: false
      });
      fabricInstanceRef.current.add(pathObj);
      fabricInstanceRef.current.renderAll();
    }
  };

  const exportBoardImage = async (format = 'png') => {
    if (!fabricInstanceRef.current) await initBoardCanvas();
    if (!fabricInstanceRef.current) return;
    setBoardExporting(true);
    try {
      const width = fabricInstanceRef.current.getWidth();
      const height = fabricInstanceRef.current.getHeight();
      const dataUrl = fabricInstanceRef.current.toDataURL({ format: 'png', multiplier: 2 });
      if (format === 'png') {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `pizarra-${aulaEstado?.aulaId || 'aula'}.png`;
        link.click();
      } else {
        const pdf = new jsPDF({
          orientation: width >= height ? 'l' : 'p',
          unit: 'px',
          format: [width, height]
        });
        pdf.addImage(dataUrl, 'PNG', 0, 0, width, height);
        pdf.save(`pizarra-${aulaEstado?.aulaId || 'aula'}.pdf`);
      }
    } catch (err) {
      setMessagesError('No pudimos exportar la pizarra.');
    } finally {
      setBoardExporting(false);
    }
  };

  const triggerBeep = async () => {
    if (chatMutedRef.current) return;
    try {
      if (!audioCtxRef.current && typeof window !== 'undefined' && 'AudioContext' in window) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx) {
        if (ctx.state === 'suspended') await ctx.resume();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(640, ctx.currentTime);
        osc.frequency.linearRampToValueAtTime(880, ctx.currentTime + 0.18);
        gain.gain.setValueAtTime(0.08, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.004, ctx.currentTime + 0.18);
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.18);
      } else if (notificationSoundRef.current) {
        notificationSoundRef.current.currentTime = 0;
        notificationSoundRef.current.play().catch(() => {});
      }
    } catch (_) {
      // ignore
    }
  };

  useEffect(() => {
    const setupCallHub = async () => {
      const privateAccessReady = Boolean(
        aulaEstado &&
        !aulaEstado.aulaPublica &&
        (aulaEstado.requiereAutenticacion ?? aulaEstado.RequiereAutenticacion) &&
        activeAulaAuthToken &&
        activeAulaAuthUser
      );
      if (!videoEnabled || !aulaEstado?.aulaId || callConnectionRef.current) return;
      if ((aulaEstado && !aulaEstado.aulaPublica && (aulaEstado.requiereAutenticacion ?? aulaEstado.RequiereAutenticacion)) && !privateAccessReady) return;
      const hubBase =
        resolveHubBaseUrl();
      const conn = new HubConnectionBuilder()
        .withUrl(`${hubBase}/hubs/aula-call`, {
          withCredentials: true,
          accessTokenFactory: () => (isPrivateAuthenticated ? activeAulaAuthToken || '' : '')
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();
      conn.on('VideoCallStarted', (payload) => {
        if (!payload) return;
        setCallInfo({
          startedBy: payload.startedBy || 'Anónimo',
          roomUrl: payload.roomUrl,
          startedAt: payload.startedAt
        });
      });
      conn.on('VideoCallEnded', () => {
        setCallInfo(null);
      });
      try {
        await conn.start();
        await conn.invoke('JoinAulaCall', aulaEstado.aulaId);
        callConnectionRef.current = conn;
      } catch (err) {
        console.warn('No se pudo conectar al hub de videollamada', err);
      }
    };
    setupCallHub();
  }, [videoEnabled, aulaEstado, activeAulaAuthToken, activeAulaAuthUser]);

  const iniciarVideollamada = async (nombreParam) => {
    if (!callConnectionRef.current || !aulaEstado?.aulaId) return;
    const nombre =
      (nombreParam ?? callNameInput ?? participantDisplayName ?? '').trim() || 'Anónimo';
    const room = `${jitsiBase}/aula-${aulaEstado.aulaId}-${Date.now()}`;
    try {
      await callConnectionRef.current.invoke('StartCall', aulaEstado.aulaId, nombre, room);
      setCallInfo({
        startedBy: nombre,
        roomUrl: room,
        startedAt: new Date().toISOString()
      });
      emitTelemetry('aula.call_started', {
        origen: 'videollamada_aula',
        displayName: nombre
      });
      emitTelemetry('aula.call_joined', {
        origen: 'videollamada_aula',
        displayName: nombre
      });
      window.open(room, '_blank');
    } catch (err) {
      setMessagesError('No pudimos iniciar la videollamada.');
    }
  };

  const handleJoinExistingCall = () => {
    if (!callInfo?.roomUrl) return;

    const nombre =
      (participantDisplayName || chatDisplayName || callNameInput || '').trim() || 'Anónimo';

    emitTelemetry('aula.call_joined', {
      origen: 'videollamada_aula',
      displayName: nombre
    });

    window.open(callInfo.roomUrl, '_blank');
  };

  const handleOpenCallModal = () => {
    if (isPrivateAuthenticated) {
      void iniciarVideollamada(participantDisplayName);
      return;
    }
    setCallNameInput(chatDisplayName.trim() || '');
    setCallNameModalOpen(true);
  };

  const playNotification = (payload) => {
    triggerBeep();
    if (notificationsAllowed && typeof Notification !== 'undefined') {
      try {
        new Notification('Nuevo mensaje en el chat', {
          body: `${payload.autor}: ${payload.contenido}`.slice(0, 80),
          silent: chatMutedRef.current,
          tag: `aula-chat-${aulaId}`
        });
      } catch (_) {}
    }
  };

  const startChatConnection = async () => {
    const displayName = (participantDisplayName || chatDisplayName || '').trim();
    if (!aulaEstado?.aulaId || !displayName) return;
    if (connectionRef.current) return;
    setChatConnecting(true);
    try {
      await ensureNotificationPermission();

      const hubBase =
        resolveHubBaseUrl();

      const connection = new HubConnectionBuilder()
        .withUrl(`${hubBase}/hubs/aula-chat`, {
          withCredentials: true,
          accessTokenFactory: () => (isPrivateAuthenticated ? activeAulaAuthToken || '' : '')
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();

      connection.on('ReceiveMessage', (payload) => {
        setChatMessages((prev) => [...prev, { ...payload, tipo: 'mensaje' }]);
        playNotification(payload);
        scrollChatToBottom();
      });
      connection.on('UserJoined', (name, fechaUtc) => {
        setChatMessages((prev) => [...prev, { autor: 'sistema', contenido: `${name} se sumó al chat`, fechaUtc, tipo: 'sistema' }]);
        setChatUsers((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.includes(name) ? list : [...list, name];
        });
        scrollChatToBottom();
      });
      connection.on('UserLeft', (name, fechaUtc) => {
        setChatMessages((prev) => [...prev, { autor: 'sistema', contenido: `${name} salió del chat`, fechaUtc, tipo: 'sistema' }]);
        setChatUsers((prev) => (Array.isArray(prev) ? prev.filter((u) => u !== name) : []));
        scrollChatToBottom();
      });
      connection.on('ConnectedUsers', (users) => {
        setChatUsers(Array.isArray(users) ? users : []);
      });

      await connection.start();
      await connection.invoke('JoinAula', aulaEstado.aulaId, displayName);
      setChatUsers((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const me = displayName;
        return list.includes(me) ? list : [...list, me];
      });
      connectionRef.current = connection;
      setChatOpen(true);
      emitTelemetry('aula.chat_started', {
        origen: 'chat_grupal',
        displayName: participantDisplayName
      });

      // Pull de lista por las dudas
      try {
        const users = await connection.invoke('GetConnectedUsers', aulaEstado.aulaId);
        if (Array.isArray(users)) setChatUsers(users);
      } catch (_) {}
    } catch (err) {
      setMessagesError('No pudimos conectar al chat del aula.');
    } finally {
      setChatConnecting(false);
    }
  };

  // --------- BOARD (SignalR) ---------
  const startBoardConnection = async () => {
    const name = (participantDisplayName || boardNameInput || '').trim();
    if (!aulaEstado?.aulaId || !name) {
      setBoardNameModalOpen(true);
      return;
    }
    if (!chatDisplayName.trim()) {
      setChatDisplayName(name);
    }
    if (boardConnectionRef.current) return;
    myBoardNameRef.current = name;
    setBoardConnecting(true);
    try {
      const hubBase =
        resolveHubBaseUrl();

      const connection = new HubConnectionBuilder()
        .withUrl(`${hubBase}/hubs/aula-board`, {
          withCredentials: true,
          accessTokenFactory: () => (isPrivateAuthenticated ? activeAulaAuthToken || '' : '')
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();

      connection.on('UserJoinedBoard', (name) => {
        if (typeof name === 'string' && name.startsWith('__observer__')) return;
        setBoardUsers((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.includes(name) ? list : [...list, name];
        });
        if (!boardStartedBy) {
          setBoardStartedBy(name);
          setBoardStatusMsg(`${name} inició una pizarra colaborativa a las ${formatDateTime(new Date().toISOString())}`);
        }
        // si soy quien inició, envío instantánea al que se suma para que vea el estado actual
        const iamOwner =
          myBoardNameRef.current &&
          boardStartedBy &&
          myBoardNameRef.current.trim().toLowerCase() === boardStartedBy.trim().toLowerCase();
        if (iamOwner && name !== myBoardNameRef.current) {
          const snapshot = fabricInstanceRef.current?.toJSON(BOARD_OBJECT_JSON_PROPS);
          if (snapshot) {
            snapshot.background = fabricInstanceRef.current?.backgroundColor || '#ffffff';
            sendBoardAction({ type: 'snapshot', snapshot });
          }
        }
      });
      connection.on('UserLeftBoard', (name) => {
        if (typeof name === 'string' && name.startsWith('__observer__')) return;
        setBoardUsers((prev) => {
          const filtered = Array.isArray(prev) ? prev.filter((u) => u !== name) : [];
          // no forzamos estado a "nadie" aquí para evitar falsos vacíos; lo maneja la sonda
          return filtered;
        });
      });
      connection.on('ConnectedUsersBoard', (users) => {
        const cleaned = cleanBoardUsers(users);
        setBoardUsers(cleaned);
        if (cleaned.length > 0 && !boardStartedBy) {
          setBoardStartedBy(cleaned[0]);
          setBoardStatusMsg(`${cleaned[0]} inició una pizarra colaborativa a las ${formatDateTime(new Date().toISOString())}`);
        }
        if (cleaned.length === 0) {
          setBoardStartedBy('');
          setBoardStatusMsg('Nadie inició la pizarra aún.');
        }
      });
      connection.on('BoardAction', (payload) => {
        try {
          const data = JSON.parse(payload.payload || '{}');
          if (payload?.autor !== 'system' && data.sourceId === boardActionSourceRef.current) return;
          if (data.type === 'clear') {
            clearBoardLocal();
            return;
          }
          if (data.type === 'snapshot-request') {
            if (fabricInstanceRef.current) {
              const objs = fabricInstanceRef.current.getObjects() || [];
              if (objs.length > 0) {
                const snapshot = fabricInstanceRef.current.toJSON(BOARD_OBJECT_JSON_PROPS);
                if (snapshot) {
                  snapshot.background = fabricInstanceRef.current.backgroundColor || '#ffffff';
                  sendBoardAction({ type: 'snapshot', snapshot });
                }
              }
            }
            return;
          }
          if (data.type === 'snapshot') {
            handleRemoteFabricObject(data);
            return;
          }
          if (data.type === 'fabric-path' || data.object || data.path) {
            handleRemoteFabricObject(data);
          }
        } catch (_) {
          /* ignore */
        }
      });

      await connection.start();
      await connection.invoke('JoinBoard', aulaEstado.aulaId, name);
      await initBoardCanvas();
      boardConnectionRef.current = connection;
      setBoardOpen(true);
      emitTelemetry('aula.board_joined', {
        origen: 'pizarra_aula',
        displayName: name
      });
      if (!boardStartedBy) {
        emitTelemetry('aula.board_started', {
          origen: 'pizarra_aula',
          displayName: name
        });
      }
      if (!boardStartedBy) {
        setBoardStatusMsg('Conectado a la pizarra colaborativa');
      }

      try {
        const users = await connection.invoke('GetConnectedUsers', aulaEstado.aulaId);
        if (Array.isArray(users)) setBoardUsers(users);
      } catch (_) {}
      // Solicitar snapshot al host por si ya hay contenido
      sendBoardAction({ type: 'snapshot-request' });
    } catch (err) {
      setMessagesError('No pudimos conectar a la pizarra.');
    } finally {
      setBoardConnecting(false);
    }
  };

  const leaveBoard = async () => {
    const iAmOwner =
      myBoardNameRef.current &&
      boardStartedBy &&
      myBoardNameRef.current.trim().toLowerCase() === boardStartedBy.trim().toLowerCase();
    if (iAmOwner) {
      const confirmEnd = window.confirm(
        'Si salís, la sesión de pizarra se cerrará para todos y se perderá el contenido no exportado. ¿Cerrar ahora?'
      );
      if (!confirmEnd) return;
      // Limpia para todos
      const snapshot = { objects: [], background: '#ffffff' };
      await sendBoardAction({ type: 'snapshot', snapshot });
    }
    if (boardConnectionRef.current) {
      try {
        await boardConnectionRef.current.invoke('LeaveBoard', aulaEstado.aulaId);
      } catch (_) {}
      try {
        await boardConnectionRef.current.stop();
      } catch (_) {}
      boardConnectionRef.current = null;
    }
    setBoardOpen(false);
    setBoardUsers([]);
    setBoardStartedBy('');
    setBoardStatusMsg('Nadie inició la pizarra aún.');
    disposeBoardCanvas();
  };

  const sendBoardClear = async () => {
    clearBoardLocal();
    await sendBoardAction({ type: 'clear' });
  };

  const leaveChat = async () => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.stop();
      } catch (_) { /* ignore */ }
      connectionRef.current = null;
    }
    setChatOpen(false);
    setChatMessages([]);
    setChatUsers([]);
  };

  const handleChatInputChange = (e) => {
    setChatInput(e.target.value);
    if (chatInputRef.current) {
      chatInputRef.current.style.height = 'auto';
      chatInputRef.current.style.height = `${Math.min(chatInputRef.current.scrollHeight, 140)}px`;
    }
  };

  const handleSendChatMessage = async () => {
    if (!connectionRef.current || !chatInput.trim()) return;
    const text = chatInput.trim();
    setChatInput('');
    if (chatInputRef.current) {
      chatInputRef.current.style.height = '40px';
    }
    try {
      await connectionRef.current.invoke('SendMessage', aulaEstado.aulaId, participantDisplayName.trim(), text);
    } catch (err) {
      setMessagesError('No pudimos enviar el mensaje del chat.');
    }
  };

  const handleSelectAssistant = (assistant) => {
    setSelectedAssistant(assistant);
    setAssistantPassword('');
    setAssistantPasswordError('');
  };

  const handleStartChat = async () => {
    if (!selectedAssistant) return;
    const requierePassword = selectedAssistant?.requierePassword ?? selectedAssistant?.RequierePassword ?? false;
    const esGPTClasico = selectedAssistant?.esGPTClasico ?? selectedAssistant?.EsGPTClasico ?? false;
    const urlPublicaClasico = selectedAssistant?.gptClasicoURLPublica || selectedAssistant?.GPTClasicoURLPublica || '';
    const creacionFinalizada = selectedAssistant?.gptClasicoCreacionFinalizada ?? selectedAssistant?.GPTClasicoCreacionFinalizada ?? false;
    if (requierePassword) {
      if (!assistantPassword.trim()) {
        setAssistantPasswordError('Ingresa el código del asistente para continuar.');
        return;
      }
      if (!colegioId) {
        setAssistantPasswordError('No se pudo identificar el colegio.');
        return;
      }
      setAssistantValidating(true);
      setAssistantPasswordError('');
      try {
        const assistantId = selectedAssistant.idOpenAIAssistant || selectedAssistant.IdOpenAIAssistant || selectedAssistant.id;
        const result = await materiaService.validateAsistentePassword(assistantId, colegioId, assistantPassword.trim());
        const valido = result?.valido ?? result?.Valido;
        if (!valido) {
          setAssistantPasswordError('El código ingresado no es correcto.');
          return;
        }
        setChatPassword(assistantPassword.trim());
      } catch (error) {
        setAssistantPasswordError(error.message || 'No pudimos validar el código.');
        return;
      } finally {
        setAssistantValidating(false);
      }
    } else {
      setChatPassword('');
    }

    // Para asistentes IA: solo permitimos seguir si cumple condiciones mínimas (URL pública disponible)
    if (esGPTClasico && (!creacionFinalizada || !urlPublicaClasico)) {
      setAssistantPasswordError('Este asistente clásico aún no está disponible.');
      return;
    }

    setShowConsent(true);
  };

  const handleConsentAccept = () => {
    setShowConsent(false);
    const esGPTClasico = selectedAssistant?.esGPTClasico ?? selectedAssistant?.EsGPTClasico ?? false;
    const urlPublicaClasico = selectedAssistant?.gptClasicoURLPublica || selectedAssistant?.GPTClasicoURLPublica || '';
    const creacionFinalizada = selectedAssistant?.gptClasicoCreacionFinalizada ?? selectedAssistant?.GPTClasicoCreacionFinalizada ?? false;
    const assistantName = selectedAssistant?.nombre || selectedAssistant?.Nombre || 'Asistente';
    const assistantType = esGPTClasico ? 'gpt_clasico' : 'openai';

    emitTelemetry('aula.assistant_opened', {
      origen: assistantType === 'gpt_clasico' ? 'assistant_gpt_clasico' : 'assistant_openai',
      assistantId: getAssistantId(selectedAssistant),
      assistantName,
      assistantType
    });

    emitTelemetry('aula.chat_started', {
      origen: assistantType === 'gpt_clasico' ? 'assistant_gpt_clasico' : 'assistant_openai',
      assistantId: getAssistantId(selectedAssistant),
      assistantName,
      assistantType
    });

    if (esGPTClasico && creacionFinalizada && urlPublicaClasico) {
      window.open(urlPublicaClasico, '_blank', 'noopener,noreferrer');
      setShowChat(false);
    } else {
      setShowChat(true);
    }
  };

  useEffect(() => {
    if (!selectedAssistantId) {
      return;
    }

    const selectedStillExists = assistants.some((assistant) => getAssistantId(assistant) === selectedAssistantId);
    if (!selectedStillExists) {
      setSelectedAssistant(null);
      setAssistantPassword('');
      setAssistantPasswordError('');
      setShowChat(false);
    }
  }, [assistants, selectedAssistantId]);

  const renderAssistantPanel = (title, panelAssistants, emptyMessage) => (
    <aside className="assistant-list">
      <div className="assistant-list-header">
        <h3>{title}</h3>
        <span>{panelAssistants.length}</span>
      </div>
      <div className="assistant-items">
        {panelAssistants.map((assistant) => {
          const id = getAssistantId(assistant);
          const nombre = assistant.nombre || assistant.Nombre || 'Asistente';
          const descripcion = assistant.descripcion || assistant.Descripcion || '';
          const requierePassword = assistant.requierePassword ?? assistant.RequierePassword ?? false;
          return (
            <button
              key={id || nombre}
              className={`assistant-item ${selectedAssistantId === id ? 'active' : ''}`}
              onClick={() => handleSelectAssistant(assistant)}
            >
              <div>
                <h4>{nombre}</h4>
                <p>{descripcion || 'Sin descripción.'}</p>
              </div>
              <span className={`assistant-badge ${requierePassword ? 'lock' : 'open'}`}>
                {requierePassword ? 'Privado' : 'Público'}
              </span>
            </button>
          );
        })}
        {panelAssistants.length === 0 && (
          <div className="assistant-empty">
            <p>{emptyMessage}</p>
          </div>
        )}
      </div>
    </aside>
  );

  // ----- CAPTCHA Y MENSAJES -----
  // Carga del script Turnstile y render del captcha (una sola vez)
  useEffect(() => {
    if (!requiresAccessCaptcha) return;
    if (window.turnstile || document.getElementById('turnstile-script')) return;
    const script = document.createElement('script');
    script.id = 'turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, [requiresAccessCaptcha]);

  useEffect(() => {
    if (!requiresAccessCaptcha) return;
    if (!aulaId) return;
    if (captchaWidgetId.current) return;
    const timer = setInterval(() => {
      if (window.turnstile && captchaRef.current && !captchaWidgetId.current) {
        captchaWidgetId.current = window.turnstile.render(captchaRef.current, {
          sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAACp7NEtmVW3l14P',
          callback: async (token) => {
            setCaptchaError('');
            try {
              await AulaService.validatePublicCaptcha(token);
              setAccessCaptchaToken(token);
              setCaptchaToken(token);
              if (aulaId && shouldUsePublicFlow) {
                loadMessages(token, messagesSearch);
              }
            } catch (error) {
              setAccessCaptchaToken('');
              setCaptchaToken('');
              setCaptchaError(error.message || 'Captcha no válido, refresca e intenta de nuevo.');
              if (window.turnstile && captchaWidgetId.current) {
                window.turnstile.reset(captchaWidgetId.current);
              }
            }
          },
          'error-callback': () => setCaptchaError('Captcha no válido, refresca e intenta de nuevo.')
        });
        clearInterval(timer);
      }
    }, 200);
    return () => clearInterval(timer);
  }, [aulaId, messagesSearch, requiresAccessCaptcha, shouldUsePublicFlow]);

  // Render captcha para respuesta (se genera cada vez que se elige un mensaje)
  useEffect(() => {
    if (!replyParent || isPrivateAuthenticated || !shouldUsePublicFlow) {
      if (replyCaptchaWidgetId.current && window.turnstile) {
        window.turnstile.reset(replyCaptchaWidgetId.current);
      }
      setReplyCaptchaToken('');
      return;
    }
    const timer = setInterval(() => {
      if (window.turnstile && replyCaptchaRef.current) {
        if (replyCaptchaWidgetId.current) {
          window.turnstile.reset(replyCaptchaWidgetId.current);
        }
        replyCaptchaWidgetId.current = window.turnstile.render(replyCaptchaRef.current, {
          sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAACp7NEtmVW3l14P',
          callback: (token) => setReplyCaptchaToken(token),
          'error-callback': () => setMessagesError('Captcha inválido, intenta nuevamente.')
        });
        clearInterval(timer);
      }
    }, 200);
    return () => clearInterval(timer);
  }, [replyParent, isPrivateAuthenticated, shouldUsePublicFlow]);

  const loadMessages = async (token, searchTerm = '') => {
    if (!messagesEnabled) return;
    if (!aulaId) return;
    if (!isPrivateAuthenticated && !token) return;
    setMessagesLoading(true);
    setMessagesError('');
    try {
      const response = isPrivateAuthenticated
        ? await AulaService.getAulaMensajesPrivados({
            aulaId,
            token: activeAulaAuthToken,
            search: searchTerm || undefined
          })
        : await AulaService.getAulaPublicaMensajes({
            aulaId,
            captchaToken: token,
            search: searchTerm || undefined
          });
      const items = response?.items || response?.Items || [];
      setMessages(items);
      setMessagesTotal(response?.total ?? response?.Total ?? items.length);
    } catch (error) {
      // Si la API devuelve 403 por feature flag deshabilitada, ocultamos la sección
      const msg = error?.message || '';
      if (msg.toLowerCase().includes('403') || msg.toLowerCase().includes('deshabilitada')) {
        setMessagesEnabled(false);
        setMessagesError('');
      } else {
        setMessagesError(msg || 'No pudimos obtener los mensajes.');
      }
    } finally {
      setMessagesLoading(false);
    }
  };

  const loadArchivosPublicos = async () => {
    if (!filesEnabled) return;
    if (!aulaId) return;
    if (!isPrivateAuthenticated && !accessCaptchaToken) return;
    setFilesLoading(true);
    setFilesError('');
    try {
      const resp = isPrivateAuthenticated
        ? await AulaService.getAulaPrivadaArchivos(aulaId, activeAulaAuthToken)
        : await AulaService.getAulaPublicaArchivos(aulaId);
      if (resp?.ok === false) throw new Error(resp?.error || 'No pudimos obtener los archivos.');
      const list = resp?.items || resp?.Items || resp?.archivos || resp?.Archivos || resp || [];
      setFiles(Array.isArray(list) ? list : []);
    } catch (error) {
      const msg = error?.message || '';
      if (msg.toLowerCase().includes('403') || msg.toLowerCase().includes('deshabilitada')) {
        setFilesEnabled(false);
        setFilesError('');
      } else {
        setFilesError(msg || 'No pudimos obtener los archivos.');
      }
    } finally {
      setFilesLoading(false);
    }
  };

  const loadLinksPublicos = async () => {
    if (!linksEnabled) return;
    if (!aulaId) return;
    if (!isPrivateAuthenticated && !accessCaptchaToken) return;
    setLinksLoading(true);
    setLinksError('');
    try {
      const resp = isPrivateAuthenticated
        ? await AulaService.getAulaPrivadaLinks(aulaId, activeAulaAuthToken)
        : await AulaService.getAulaPublicaLinks(aulaId);
      if (resp?.ok === false) throw new Error(resp?.error || 'No pudimos obtener los links.');
      const list = resp?.items || resp?.Items || resp?.links || resp?.Links || resp || [];
      setLinks(Array.isArray(list) ? list.map(normalizeLinkResource) : []);
    } catch (error) {
      const msg = error?.message || '';
      if (msg.toLowerCase().includes('403') || msg.toLowerCase().includes('no están visibles')) {
        setLinksEnabled(false);
        setLinksError('');
      } else {
        setLinksError(msg || 'No pudimos obtener los links.');
      }
    } finally {
      setLinksLoading(false);
    }
  };

  useEffect(() => {
    if (filesEnabled && aulaId && (accessCaptchaToken || isPrivateAuthenticated)) {
      loadArchivosPublicos();
    }
  }, [filesEnabled, accessCaptchaToken, aulaId, isPrivateAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (linksEnabled && aulaId && (accessCaptchaToken || isPrivateAuthenticated)) {
      loadLinksPublicos();
    }
  }, [linksEnabled, accessCaptchaToken, aulaId, isPrivateAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!aulaId || !messagesEnabled || !isPrivateAuthenticated) return;
    loadMessages('', messagesSearch);
  }, [aulaId, messagesEnabled, isPrivateAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedLinkResource) {
      setLinkViewerLoading(false);
    }
  }, [selectedLinkResource]);

  const trackFileDownload = ({ fileId, fileName, downloadType }) => {
    emitTelemetry('aula.file_downloaded', {
      origen: 'archivos_aula',
      fileId,
      fileName,
      downloadType
    });
  };

  const renderMessageTree = (list, level = 0) =>
    (list || []).map((msg, idx) => (
      <div
        key={msg.id || msg.Id}
        className={`aula-message-item ${idx % 2 === 0 ? 'alt' : ''}`}
        style={{ marginLeft: level * 12 }}
      >
        <div className="aula-message-header">
          <div className="aula-message-meta">
            <span className="aula-message-title">{msg.titulo || msg.Titulo || 'Sin título'}</span>
            <span className="aula-message-author">
              {msg.nombreRemitente || msg.NombreRemitente || msg.autorNombre || msg.AutorNombre || 'Anónimo'}
            </span>
            {msg.fechaAlta && (
              <span className="aula-message-date">
                {new Date(msg.fechaAlta || msg.FechaAlta).toLocaleString()}
              </span>
            )}
          </div>
          <div className="message-actions">
            {msg.respuestas?.length > 0 && (
              <button
                className="reply-toggle"
                onClick={() => {
                  const id = msg.id || msg.Id;
                  setExpandedMessages((prev) => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next;
                  });
                }}
              >
                <span className="badge">{msg.respuestas.length}</span>
                {expandedMessages.has(msg.id || msg.Id) ? 'Ocultar respuestas' : 'Ver respuestas'}
              </button>
            )}
            <button
              className="reply-chip"
              onClick={() => {
                setReplyParent(msg);
                setReplyHtml('');
                setReplyName(isPrivateAuthenticated ? participantDisplayName : '');
              }}
            >
              Responder
            </button>
          </div>
        </div>
        <div
          className="aula-message-body"
          dangerouslySetInnerHTML={{ __html: msg.contenidoHtml || msg.ContenidoHtml || '' }}
        />
        {msg.respuestas?.length > 0 && expandedMessages.has(msg.id || msg.Id) && (
          <div className="aula-replies-block">
            {renderMessageTree(msg.respuestas, level + 1)}
          </div>
        )}
      </div>
    ));

  const aulaReady = Boolean(aulaDetalle && (!shouldUsePublicFlow || accessCaptchaToken || isPrivateAuthenticated));
  const aulaEsPublica = Boolean(aulaDetalle?.aulaPublica ?? aulaDetalle?.AulaPublica);
  const navItems = aulaDetalle ? [
    { key: 'inicio', label: 'Inicio', icon: 'fa-house' },
    showAssistantsSection && {
      key: 'asistentes',
      label: 'Asistentes IA',
      icon: 'fa-robot',
      description: 'Conversá con los asistentes del aula.',
      count: (classicAgentsEnabled ? classicAssistants.length : 0) + (openAIChatsEnabled ? openAIAssistants.length : 0)
    },
    filesEnabled && { key: 'archivos', label: 'Archivos', icon: 'fa-folder-open', description: 'Material para descargar.', count: archivosDisponibles },
    linksEnabled && { key: 'links', label: 'Links', icon: 'fa-link', description: 'Recursos web compartidos por el docente.', count: linksDisponibles },
    boardEnabled && { key: 'pizarra', label: 'Pizarra', icon: 'fa-chalkboard', description: boardStatusMsg, live: boardOpen || Boolean(boardStartedBy) },
    chatEnabled && { key: 'chat', label: 'Chat', icon: 'fa-comments', description: !chatOpen && chatLive ? `${chatPresenceUsers.length} conectados ahora.` : 'Conversación en vivo del aula.', live: chatLive },
    videoEnabled && {
      key: 'video',
      label: 'Videollamada',
      icon: 'fa-video',
      description: callInfo ? `Sala abierta por ${callInfo.startedBy}` : 'Iniciar o unirse a la sala en vivo.',
      live: Boolean(callInfo)
    },
    messagesEnabled && { key: 'foro', label: 'Foro', icon: 'fa-bullhorn', description: 'Mensajes y respuestas del aula.', count: messagesTotal }
  ].filter(Boolean) : [];
  const currentSection = navItems.some((item) => item.key === activeSection) ? activeSection : 'inicio';
  const goToSection = (key) => {
    setActiveSection(key);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="aula-public-page aula-shell">
      <header className="aula-public-header">
        <div className="header-brand">
          <img src={logoAnimacion} alt="aluxIA" />
          <div>
            <p>aluxIA</p>
            <span>Acceso a aula</span>
          </div>
        </div>
        <div className="header-info">
          {aulaColegio && <span>{aulaColegio}</span>}
          {!forceAuthenticatedAccess && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/alumno/panel')}
            >
              Volver
            </button>
          )}
        </div>
      </header>

      <div className={`aula-layout ${navItems.length > 1 ? '' : 'no-nav'}`}>
        {navItems.length > 1 && <AulaNav items={navItems} active={currentSection} onSelect={goToSection} />}

        <main className="aula-public-content">
          <section className="aula-captcha-gate">
            {requiresAccessCaptcha && !accessCaptchaToken && !isPrivateAuthenticated && (
              <div className="aula-captcha-card">
                <h3>Verificación</h3>
                <p>Completa el captcha para acceder al aula.</p>
                <div ref={captchaRef} className="turnstile-slot"></div>
                {captchaError && <div className="aula-error small">{captchaError}</div>}
              </div>
            )}
          </section>

          {aulaReady && (
            <div className="aula-home" hidden={currentSection !== 'inicio'}>
              <section className="aula-hero">
                <span className="aula-hero-badge">
                  <i className={`fas ${aulaEsPublica ? 'fa-globe' : 'fa-lock'}`} aria-hidden="true" />
                  {aulaEsPublica ? 'Aula pública' : 'Aula privada'}
                </span>
                <h1>{aulaNombre}</h1>
                {aulaDescripcion && <p className="aula-hero-desc">{aulaDescripcion}</p>}
                <dl className="aula-hero-meta">
                  <div>
                    <dt>Docente</dt>
                    <dd>{aulaProfesor}</dd>
                  </div>
                  <div>
                    <dt>Materia</dt>
                    <dd>{aulaMateria || '—'}</dd>
                  </div>
                  {(aulaDetalle?.nombreCurso || aulaDetalle?.NombreCurso) && (
                    <div>
                      <dt>Curso</dt>
                      <dd>{aulaDetalle?.nombreCurso || aulaDetalle?.NombreCurso}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Ciclo lectivo</dt>
                    <dd>{aulaCiclo || '—'}</dd>
                  </div>
                </dl>
              </section>
              <AulaOverview items={navItems} onSelect={goToSection} />
            </div>
          )}

          {loadingEstado && (
            <div className="aula-loading">
              <div className="spinner"></div>
              <p>Verificando el estado del aula...</p>
            </div>
          )}

          {!loadingEstado && estadoError && (
            <div className="aula-error">
              <h3>No pudimos acceder al aula</h3>
              <p>{estadoError}</p>
            </div>
          )}

          {!loadingEstado && aulaEstado && !aulaEstado.aulaPublica && !aulaDetalle && !requiresPrivateAuthentication && (
            <div className="aula-error">
              <h3>No pudimos acceder al aula privada</h3>
              <p>Las aulas privadas ahora requieren un alumno autenticado y un curso asociado al aula.</p>
            </div>
          )}

          {loadingDetalle && (
            <div className="aula-loading">
              <div className="spinner"></div>
              <p>Cargando información del aula...</p>
            </div>
          )}

          {aulaDetalle && showAssistantsSection && (
            <AulaSection
              active={currentSection === 'asistentes'}
              icon="fa-robot"
              title="Asistentes IA"
              subtitle="Elegí un asistente para ver sus detalles e iniciar la conversación."
            >
              <div className="aula-chat-layout">
                <div className="assistant-lists-grid">
                  {classicAgentsEnabled && renderAssistantPanel(
                    'Asistentes IA disponibles',
                    classicAssistants,
                    'No hay asistentes IA disponibles en esta aula.'
                  )}
                  {openAIChatsEnabled && renderAssistantPanel(
                    'Asistentes OpenAI disponibles',
                    openAIAssistants,
                    'No hay asistentes OpenAI disponibles en esta aula.'
                  )}
                </div>

                {selectedAssistant ? (
                  <div className="assistant-detail">
                    <button
                      className="assistant-close"
                      type="button"
                      aria-label="Cerrar detalle de asistente"
                      onClick={() => {
                        setSelectedAssistant(null);
                        setAssistantPassword('');
                        setAssistantPasswordError('');
                      }}
                    >
                      ✕
                    </button>
                    <div className="assistant-detail-header">
                      <h2>{selectedAssistant.nombre || selectedAssistant.Nombre || 'Asistente'}</h2>
                      <p>{selectedAssistant.descripcion || selectedAssistant.Descripcion || 'Sin descripción'}</p>
                    </div>
                    {(selectedAssistant.requierePassword ?? selectedAssistant.RequierePassword) && (
                      <div className="assistant-password">
                        <label>Este asistente requiere código de acceso</label>
                        <input
                          type="password"
                          value={assistantPassword}
                          onChange={(e) => setAssistantPassword(e.target.value)}
                          placeholder="Código del asistente"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleStartChat();
                            }
                          }}
                        />
                        {assistantPasswordError && <span className="error-text">{assistantPasswordError}</span>}
                      </div>
                    )}
                    <button className="primary-cta" onClick={handleStartChat} disabled={assistantValidating}>
                      {assistantValidating ? 'Validando...' : 'Iniciar conversación'}
                    </button>
                  </div>
                ) : (
                  <div className="assistant-detail assistant-detail-empty">
                    <div>
                      <h3>Selecciona un asistente</h3>
                      <p>Elige un asistente IA u OpenAI para ver sus detalles, aceptar el consentimiento e iniciar la conversación.</p>
                    </div>
                  </div>
                )}
              </div>
            </AulaSection>
          )}

          {aulaDetalle && filesEnabled && (
            <AulaSection
              active={currentSection === 'archivos'}
              icon="fa-folder-open"
              title="Archivos"
              subtitle={`${archivosDisponibles} archivos disponibles para descarga`}
            >
              {filesLoading && (
                <div className="aula-loading soft">
                  <div className="spinner"></div>
                  <p>Cargando archivos...</p>
                </div>
              )}
              {!filesLoading && filesError && <div className="aula-error small">{filesError}</div>}
              {!filesLoading && !filesError && (
                <>
                  <div className="files-actions-row">
                    <button
                      className="btn-primary"
                      disabled={files.length === 0}
                      onClick={async () => {
                        try {
                          const blob = isPrivateAuthenticated
                            ? await AulaService.downloadAulaPrivadaZip(aulaId, activeAulaAuthToken)
                            : await AulaService.downloadAulaPublicaZip(aulaId);
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${aulaNombre.replace(/\s+/g, '-')}-archivos.zip`;
                          a.click();
                          URL.revokeObjectURL(url);
                          trackFileDownload({
                            fileName: `${aulaNombre || 'aula'}-archivos.zip`,
                            downloadType: 'zip'
                          });
                        } catch (error) {
                          setFilesError(error.message || 'No pudimos descargar el ZIP.');
                        }
                      }}
                    >
                      Descargar todos (zip)
                    </button>
                    <button className="btn-secondary" onClick={loadArchivosPublicos}>
                      Actualizar lista
                    </button>
                  </div>
                  {files.length === 0 && (
                    <div className="assistant-empty">
                      <p>No hay archivos publicados en esta aula.</p>
                    </div>
                  )}
                  {files.length > 0 && (
                    <div className="files-list">
                      {files.map((f) => (
                        <div key={f.id || f.Id} className="file-row">
                          <i className="fas fa-file-lines file-icon" aria-hidden="true" />
                          <div className="file-meta">
                            <h4>{f.nombreArchivo || f.NombreArchivo}</h4>
                            <span>{f.tamanoLegible || f.TamanoLegible || ''}</span>
                          </div>
                          <button
                            className="btn-secondary"
                            onClick={async () => {
                              try {
                                const fileId = f.id || f.Id;
                                const fileName = f.nombreArchivo || f.NombreArchivo || 'archivo';
                                const blob = isPrivateAuthenticated
                                  ? await AulaService.downloadAulaPrivadaArchivo(aulaId, fileId, activeAulaAuthToken)
                                  : await AulaService.downloadAulaPublicaArchivo(aulaId, fileId);
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = fileName;
                                a.click();
                                URL.revokeObjectURL(url);
                                trackFileDownload({
                                  fileId: String(fileId),
                                  fileName,
                                  downloadType: 'single'
                                });
                              } catch (error) {
                                setFilesError(error.message || 'No pudimos descargar el archivo.');
                              }
                            }}
                          >
                            Descargar
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </AulaSection>
          )}

          {aulaDetalle && linksEnabled && (
            <AulaSection
              active={currentSection === 'links'}
              icon="fa-link"
              title="Links"
              subtitle={`${linksDisponibles} recursos compartidos por el docente`}
            >
              {linksLoading && (
                <div className="aula-loading soft">
                  <div className="spinner"></div>
                  <p>Cargando links...</p>
                </div>
              )}

              {!linksLoading && linksError && <div className="aula-error small">{linksError}</div>}

              {!linksLoading && !linksError && (
                <>
                  <div className="files-actions-row">
                    <button className="btn-secondary" onClick={loadLinksPublicos}>
                      Actualizar lista
                    </button>
                  </div>

                  {links.length === 0 && (
                    <div className="assistant-empty">
                      <p>El docente todavía no publicó links para esta aula.</p>
                    </div>
                  )}

                  {links.length > 0 && (
                    <div className="aula-links-public-list">
                      {links.map((link) => {
                        const description = link.descripcion || 'Recurso externo compartido por el docente.';
                        const hostname = getLinkHostname(link.url);
                        return (
                          <article key={link.id} className="aula-link-public-card">
                            <div className="aula-link-public-copy">
                              <div className="aula-link-public-eyebrow">
                                <span>{hostname || 'Recurso web'}</span>
                              </div>
                              <h4>{description}</h4>
                              <p>{link.url}</p>
                            </div>
                            <div className="aula-link-public-actions">
                              <button
                                className="btn-primary"
                                onClick={() => {
                                  setLinkViewerLoading(true);
                                  setSelectedLinkResource(link);
                                }}
                              >
                                Abrir en el aula
                              </button>
                              <button
                                className="btn-secondary"
                                onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
                              >
                                Nueva pestaña
                              </button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </AulaSection>
          )}

          {aulaDetalle && boardEnabled && (
            <AulaSection
              active={currentSection === 'pizarra'}
              icon="fa-chalkboard"
              title="Pizarra colaborativa"
              subtitle={boardStatusMsg}
              status={boardOpen ? 'En vivo' : ''}
              actions={(
                <>
                  {!boardOpen && (
                    <button
                      className="btn-primary"
                      disabled={boardConnecting}
                      onClick={startBoardConnection}
                    >
                      {boardConnecting ? 'Conectando...' : boardStartedBy ? 'Unirse y participar' : 'Iniciar pizarra'}
                    </button>
                  )}
                  {boardOpen && (
                    <>
                      <button className="btn-secondary" onClick={leaveBoard}>Salir</button>
                    </>
                  )}
                </>
              )}
            >
              {!boardOpen && (
                <AulaEmptyState icon="fa-chalkboard">
                  Iniciá la pizarra o unite a la que ya está abierta para dibujar en tiempo real con el resto del aula.
                </AulaEmptyState>
              )}
              {boardOpen && (
                <div className="board-layout">
                  <div className="board-tools">
                    <label>Color</label>
                    <input type="color" value={boardColor} onChange={handleBoardColorChange} />
                    <label>Grosor</label>
                    <input
                      type="range"
                      min={1}
                      max={16}
                      value={boardWidth}
                      onChange={(e) => setBoardWidth(Number(e.target.value))}
                    />
                    <label>Tamaño texto</label>
                    <input
                      type="range"
                      min={12}
                      max={72}
                      value={boardTextSize}
                      onChange={handleBoardTextSizeChange}
                    />
                    {boardEraser && (
                      <>
                        <label>Tamaño goma</label>
                        <input
                          type="range"
                          min={4}
                          max={40}
                          value={boardEraserWidth}
                          onChange={(e) => setBoardEraserWidth(Number(e.target.value))}
                        />
                      </>
                    )}
                    <div className="board-mode-block">
                      <span className="board-mode-label">Modo de interacción</span>
                      <div className="board-toggle-row">
                        <button
                          className={`btn-chip ${!boardEraser && !boardPanMode && !boardSelectMode ? 'active' : ''}`}
                          onClick={() => {
                            setBoardEraser(false);
                            setBoardPanMode(false);
                            setBoardSelectMode(false);
                          }}
                        >
                          Dibujar
                        </button>
                        <button
                          className={`btn-chip ${boardEraser ? 'active' : ''}`}
                          onClick={() => {
                            setBoardEraser(true);
                            setBoardPanMode(false);
                            setBoardSelectMode(false);
                          }}
                        >
                          Borrar
                        </button>
                        <button
                          className={`btn-chip ${boardPanMode ? 'active' : ''}`}
                          onClick={() => {
                            setBoardPanMode(true);
                            setBoardEraser(false);
                            setBoardSelectMode(false);
                          }}
                        >
                          Mover lienzo
                        </button>
                        <button
                          className={`btn-chip ${boardSelectMode ? 'active' : ''}`}
                          onClick={() => {
                            setBoardSelectMode(true);
                            setBoardPanMode(false);
                            setBoardEraser(false);
                          }}
                        >
                          Editar objetos
                        </button>
                      </div>
                      <p className="board-mode-hint">
                        Dibujar: trazos libres • Borrar: pincel blanco • Mover: arrastra el lienzo • Editar objetos: seleccioná y transformá imágenes/textos.
                      </p>
                    </div>
                    <div className="board-export">
                      <button className="btn-secondary" disabled={boardExporting} onClick={() => exportBoardImage('png')}>
                        {boardExporting ? 'Preparando...' : 'Exportar PNG'}
                      </button>
                      <button className="btn-secondary" disabled={boardExporting} onClick={() => exportBoardImage('pdf')}>
                        {boardExporting ? 'Preparando...' : 'Exportar PDF'}
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => imageInputRef.current && imageInputRef.current.click()}
                      >
                        Insertar imagen
                      </button>
                      <button className="btn-secondary" onClick={addTextToBoard}>
                        Insertar texto
                      </button>
                    </div>
                    <div className="board-users">
                      <h4>Conectados ({boardUsers.length})</h4>
                      <ul>
                        {boardUsers.map((u, idx) => (
                          <li key={`${u}-${idx}`} className={u === participantDisplayName.trim() ? 'me' : ''}>
                            {u === participantDisplayName.trim() ? 'Yo' : u}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="board-canvas" ref={boardContainerRef}>
                    <canvas ref={boardCanvasRef} />
                    <div className="board-floating-controls" role="group" aria-label="Controles de zoom de la pizarra">
                      <button
                        type="button"
                        className="board-floating-btn"
                        aria-label="Acercar pizarra"
                        onClick={() => zoomBoardByFactor(1.2)}
                      >
                        +
                      </button>
                      <div className="board-zoom-indicator">{Math.round(boardZoom * 100)}%</div>
                      <button
                        type="button"
                        className="board-floating-btn"
                        aria-label="Alejar pizarra"
                        onClick={() => zoomBoardByFactor(1 / 1.2)}
                      >
                        -
                      </button>
                    </div>
                    <div className="board-hint">
                      En tablet/iPad: usá + / -, pellizcá con dos dedos para zoom/desplazar y activá "Mover" para arrastrar con un dedo.
                    </div>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          if (ev.target?.result) addImageToBoard(ev.target.result.toString());
                        };
                        reader.readAsDataURL(file);
                        e.target.value = '';
                      }}
                    />
                  </div>
                </div>
              )}
            </AulaSection>
          )}

          {aulaDetalle && chatEnabled && (
            <AulaSection
              active={currentSection === 'chat'}
              icon="fa-comments"
              title="Chat del aula"
              subtitle={!chatOpen && chatLive
                ? `Chat en vivo: ${chatPresenceUsers.join(', ')}`
                : 'Conversación en vivo con quienes están viendo esta aula.'}
              status={chatOpen ? 'Activo' : chatLive ? 'En vivo' : ''}
              actions={(
                <button
                  className="btn-chat-aula"
                  onClick={() => {
                    if (isPrivateAuthenticated) {
                      void startChatConnection();
                      return;
                    }
                    setChatConsent(true);
                  }}
                  disabled={chatConnecting || chatOpen}
                >
                  {chatConnecting ? 'Conectando...' : chatOpen ? 'Chat activo' : chatLive ? 'Unirse al chat' : 'Entrar al chat'}
                </button>
              )}
            >
              {!chatOpen && (
                <AulaEmptyState icon="fa-comments">
                  {chatLive
                    ? 'Hay un chat en vivo. Unite para conversar con quienes están conectados.'
                    : 'Entrá al chat para conversar en vivo y ver quiénes están conectados.'}
                </AulaEmptyState>
              )}
              {chatOpen && (
                <div className="chat-layout">
                  <aside className="chat-users">
                    <h4>Conectados ({chatUsers.length})</h4>
                    <ul>
                      {chatUsers.map((u, idx) => (
                        <li key={`${u}-${idx}`} className={u === participantDisplayName.trim() ? 'me' : ''}>
                          {u === participantDisplayName.trim() ? 'Yo' : u}
                        </li>
                      ))}
                    </ul>
                  </aside>
                  <div className="chat-window">
                    <div className="chat-messages" ref={chatScrollRef}>
                      {chatMessages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`chat-bubble ${msg.autor === participantDisplayName.trim() ? 'mine' : msg.autor === 'sistema' ? 'system' : 'other'}`}
                        >
                          <div className="chat-meta">
                            <span className="chat-author">
                              {msg.autor === 'sistema' ? 'Sistema' : msg.autor === participantDisplayName.trim() ? 'Yo' : msg.autor}
                            </span>
                            <span className="chat-time">
                              {new Date(msg.fechaUtc || Date.now()).toLocaleTimeString()}
                            </span>
                          </div>
                          <p>{msg.contenido}</p>
                        </div>
                      ))}
                    </div>
                    <div className="chat-actions">
                      <div className="chat-compose">
                        <textarea
                          ref={chatInputRef}
                          rows={1}
                          placeholder="Escribe tu mensaje..."
                          value={chatInput}
                          onChange={handleChatInputChange}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendChatMessage();
                            }
                          }}
                        />
                        <button className="btn-primary btn-send" onClick={handleSendChatMessage} disabled={!chatInput.trim()}>
                          Enviar
                        </button>
                      </div>
                      <div className="chat-actions-secondary">
                        <button className="btn-secondary" onClick={leaveChat}>
                          Salir
                        </button>
                        <button
                          className={`btn-secondary ${chatMuted ? 'btn-mute-on' : 'btn-mute-off'}`}
                          onClick={() => setChatMuted((prev) => !prev)}
                        >
                          {chatMuted ? 'Activar sonido' : 'Silenciar sonido'}
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => {
                            const lines = chatMessages.map((m) => {
                              const who = m.autor === 'sistema' ? '[sistema]' : m.autor;
                              const when = formatDateTime(m.fechaUtc || Date.now());
                              return `${when} - ${who}: ${normalizeChatExportText(m.contenido)}`;
                            });
                            const conversationText = lines.join('\n').replace(/\n/g, '\r\n');
                            const blob = new Blob([conversationText], { type: 'text/plain;charset=utf-8' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `chat-aula-${aulaId}.txt`;
                            link.click();
                            URL.revokeObjectURL(url);
                          }}
                        >
                          Descargar conversación
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </AulaSection>
          )}

          {aulaDetalle && videoEnabled && (
            <AulaSection
              active={currentSection === 'video'}
              icon="fa-video"
              title="Videollamada"
              subtitle={callInfo ? `Sala abierta por ${callInfo.startedBy}` : 'Iniciar o unirse a la sala en vivo'}
              status={callInfo ? 'Sala activa' : ''}
            >
              <div className="aula-video-section">
                {!callInfo && (
                  <div className="video-start-row">
                    <button className="btn-video-start" onClick={handleOpenCallModal}>
                      Iniciar llamada grupal
                    </button>
                    <p className="video-hint">
                      Úsalo para abrir una videollamada en vivo con todos los que están viendo esta aula.
                      Se abrirá una sala segura (Jitsi) y los demás verán un aviso para unirse. Cada llamada dura 1 hora;
                      si vence el tiempo, simplemente crea una nueva.
                    </p>
                  </div>
                )}
                {callInfo && (
                  <div className="video-banner">
                    <div>
                      <h4>Videollamada grupal iniciada por {callInfo.startedBy}</h4>
                      <p>Se creó la sala hace unos instantes. Únete para participar. La llamada se cerrará automáticamente a la hora.</p>
                    </div>
                    <div className="video-actions">
                      <button
                        className="btn-primary"
                        onClick={handleJoinExistingCall}
                      >
                        Unirse
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </AulaSection>
          )}

          {aulaDetalle && messagesEnabled && (
            <AulaSection
              active={currentSection === 'foro'}
              icon="fa-bullhorn"
              title="Foro del aula"
              subtitle={`${messagesTotal} mensajes publicados`}
              actions={(
                <div className="messages-actions">
                  <input
                    type="text"
                    placeholder="Buscar en mensajes..."
                    value={messagesSearch}
                    onChange={(e) => setMessagesSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        loadMessages(captchaToken, e.target.value);
                      }
                    }}
                    disabled={((shouldUsePublicFlow && !captchaToken) || (!shouldUsePublicFlow && !isPrivateAuthenticated)) || messagesLoading}
                  />
                  <button
                    className="btn-secondary"
                    onClick={() => loadMessages(captchaToken, messagesSearch)}
                    disabled={((shouldUsePublicFlow && !captchaToken) || (!shouldUsePublicFlow && !isPrivateAuthenticated)) || messagesLoading}
                  >
                    {messagesLoading ? 'Cargando...' : 'Actualizar'}
                  </button>
                </div>
              )}
            >
              {shouldUsePublicFlow && !captchaToken && !isPrivateAuthenticated && (
                <div className="aula-captcha-card">
                  <p>Completa el captcha para ver los mensajes.</p>
                </div>
              )}

              {messagesError && <div className="aula-error small">{messagesError}</div>}

              {(captchaToken || isPrivateAuthenticated) && !messagesLoading && messages.length === 0 && (
                <div className="assistant-empty">
                  <p>No hay mensajes publicados en esta aula.</p>
                </div>
              )}

              {messagesLoading && (
                <div className="aula-loading soft">
                  <div className="spinner"></div>
                  <p>Cargando mensajes...</p>
                </div>
              )}

              {(captchaToken || isPrivateAuthenticated) && !messagesLoading && messages.length > 0 && (
                <div className="aula-messages-list">{renderMessageTree(messages)}</div>
              )}

              {replyParent && (
                <div className="aula-reply-card">
                  <div className="aula-reply-header">
                    <div>
                      <h4>Responder a: {replyParent.titulo || replyParent.Titulo || 'Mensaje'}</h4>
                    </div>
                    <button className="btn-secondary" onClick={() => { setReplyParent(null); setReplyHtml(''); setReplyName(''); }}>Cancelar</button>
                  </div>
                  <div className="aula-reply-form">
                    {!isPrivateAuthenticated && (
                      <>
                        <label>Tu nombre (obligatorio)</label>
                        <input
                          type="text"
                          value={replyName}
                          onChange={(e) => setReplyName(e.target.value)}
                          placeholder="Ej: Juan Pérez"
                        />
                      </>
                    )}
                    <label>Tu respuesta</label>
                    <textarea
                      rows={5}
                      value={replyHtml}
                      onChange={(e) => setReplyHtml(e.target.value)}
                      placeholder="Escribe tu respuesta..."
                    />
                    {!isPrivateAuthenticated && (
                      <div className="reply-captcha-row">
                        <div ref={replyCaptchaRef} className="turnstile-slot"></div>
                      </div>
                    )}
                    <button
                      className="btn-primary"
                      disabled={(!isPrivateAuthenticated && !replyCaptchaToken) || !replyParent || (!isPrivateAuthenticated && !replyName.trim()) || messagesLoading || replySending}
                      onClick={async () => {
                        if (!isPrivateAuthenticated && !replyCaptchaToken) {
                          setMessagesError('Completa el captcha antes de enviar.');
                          return;
                        }
                        try {
                          setMessagesError('');
                          setReplySending(true);
                          if (isPrivateAuthenticated) {
                            await AulaService.responderAulaMensajePrivado({
                              aulaId,
                              token: activeAulaAuthToken,
                              idMensajePadre: replyParent.id || replyParent.Id,
                              titulo: `Re: ${replyParent.titulo || replyParent.Titulo || ''}`,
                              contenidoHtml: replyHtml
                            });
                          } else {
                            await AulaService.responderAulaMensajePublico({
                              aulaId,
                              idMensajePadre: replyParent.id || replyParent.Id,
                              titulo: `Re: ${replyParent.titulo || replyParent.Titulo || ''}`,
                              contenidoHtml: replyHtml,
                              nombreRemitente: replyName.trim(),
                              captchaToken: replyCaptchaToken
                            });
                          }
                          await loadMessages(captchaToken || replyCaptchaToken, messagesSearch);
                          setReplyParent(null);
                          setReplyHtml('');
                          setReplyName(isPrivateAuthenticated ? participantDisplayName : '');
                          setReplyCaptchaToken('');
                        } catch (error) {
                          setMessagesError(error.message || 'No pudimos enviar la respuesta.');
                        } finally {
                          setReplySending(false);
                        }
                      }}
                    >
                      Enviar respuesta
                    </button>
                  </div>
                </div>
              )}
            </AulaSection>
          )}
        </main>
      </div>

      {callNameModalOpen && (
        <div className="consent-overlay">
          <div className="consent-card">
            <h3>Iniciar videollamada</h3>
            <p>Ingresa tu nombre para que los demás sepan quién abrió la sala.</p>
            <input
              type="text"
              value={callNameInput}
              onChange={(e) => setCallNameInput(e.target.value)}
              placeholder="Tu nombre"
            />
            <div className="consent-actions">
              <button className="btn-secondary" onClick={() => setCallNameModalOpen(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={async () => {
                  setCallNameModalOpen(false);
                  await iniciarVideollamada(callNameInput);
                }}
                disabled={!callNameInput.trim() && !participantDisplayName.trim()}
              >
                Crear sala
              </button>
            </div>
          </div>
        </div>
      )}

      {showConsent && (
        <div className="consent-overlay">
          <div className="consent-card">
            <h3>Antes de comenzar</h3>
            <p>
              Este asistente puede generar información incorrecta o incompleta. Verificá siempre las respuestas con
              fuentes confiables y consultá a tu docente ante dudas importantes.
            </p>
            <div className="consent-actions">
              <button className="btn-secondary" onClick={() => setShowConsent(false)}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={handleConsentAccept}>
                Acepto y continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {chatConsent && !isPrivateAuthenticated && (
        <div className="consent-overlay">
          <div className="consent-card">
            <h3>Reglas del chat del aula</h3>
            <p>
              Usá el chat con respeto y fines educativos. Registraremos tu nombre y datos técnicos básicos de tu conexión
              para mantener el orden de la sala. Cualquier abuso puede implicar bloqueo inmediato.
            </p>
            <label>Tu nombre para mostrar</label>
            <input
              type="text"
              value={chatDisplayName}
              onChange={(e) => setChatDisplayName(e.target.value)}
              placeholder="Ej: Invitado"
            />
            <div className="consent-actions">
              <button className="btn-secondary" onClick={() => setChatConsent(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                disabled={!chatDisplayName.trim()}
                onClick={async () => {
                  setChatConsent(false);
                  // Pedir permiso de notificación y “desmutear” el beep con un gesto del usuario
                  await ensureNotificationPermission();
                  if (notificationSoundRef.current) {
                    try {
                      notificationSoundRef.current.volume = 1;
                      await notificationSoundRef.current.play().catch(() => {});
                      notificationSoundRef.current.pause();
                      notificationSoundRef.current.currentTime = 0;
                      notificationSoundRef.current.muted = false;
                    } catch (_) {}
                  }
                  await startChatConnection();
                }}
              >
                Ingresar al chat
              </button>
            </div>
          </div>
        </div>
      )}

      {boardNameModalOpen && !isPrivateAuthenticated && (
        <div className="consent-overlay">
          <div className="consent-card">
            <h3>Nombre para la pizarra</h3>
            <p>Ingresa tu nombre para que los demás sepan quién eres en la pizarra.</p>
            <input
              type="text"
              value={boardNameInput}
              onChange={(e) => setBoardNameInput(e.target.value)}
              placeholder="Ej: Invitado"
            />
            <div className="consent-actions">
              <button className="btn-secondary" onClick={() => setBoardNameModalOpen(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                disabled={!boardNameInput.trim()}
                onClick={async () => {
                  setBoardNameModalOpen(false);
                  setChatDisplayName(boardNameInput.trim());
                  await startBoardConnection();
                }}
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {replySending && (
        <div className="fullscreen-overlay">
          <div className="spinner"></div>
          <p>Respondiendo mensaje...</p>
        </div>
      )}

      {selectedLinkResource && (
        <div className="consent-overlay link-viewer-overlay">
          <div className="link-viewer-modal">
            <div className="link-viewer-header">
              <div className="link-viewer-copy">
                <span className="link-viewer-eyebrow">Link compartido por el docente</span>
                <h3>{selectedLinkResource.descripcion || 'Recurso externo del aula'}</h3>
                <p>{selectedLinkResource.url}</p>
              </div>
              <div className="link-viewer-actions">
                <button
                  className="btn-secondary"
                  onClick={() => window.open(selectedLinkResource.url, '_blank', 'noopener,noreferrer')}
                >
                  Abrir en pestaña nueva
                </button>
                <button
                  className="link-viewer-close"
                  type="button"
                  aria-label="Cerrar visor de link"
                  onClick={() => {
                    setSelectedLinkResource(null);
                    setLinkViewerLoading(false);
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="link-viewer-note">
              <strong>Descripción del docente:</strong>
              <span>{selectedLinkResource.descripcion || 'Este recurso no tiene una descripción adicional.'}</span>
            </div>

            <div className="link-viewer-frame-shell">
              {linkViewerLoading && (
                <div className="link-viewer-loading">
                  <div className="spinner"></div>
                  <p>Abriendo el recurso dentro del aula...</p>
                </div>
              )}
              <iframe
                title={selectedLinkResource.descripcion || 'Recurso externo del aula'}
                src={selectedLinkResource.url}
                className="link-viewer-frame"
                onLoad={() => setLinkViewerLoading(false)}
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>

            <div className="link-viewer-footer">
              <p>
                Si el sitio no permite visualizarse embebido en el aula, usa el botón
                {' '}
                <strong>Abrir en pestaña nueva</strong>.
              </p>
            </div>
          </div>
        </div>
      )}

      <ChatModal
        isOpen={showChat}
        onClose={() => setShowChat(false)}
        assistant={selectedAssistant}
        publicChat
        colegioId={colegioId}
        password={chatPassword}
        headerTitle={selectedAssistant?.nombre || selectedAssistant?.Nombre || 'Asistente'}
      />
    </div>
  );
};

export default AulaPublicPage;
